import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Eye, EyeOff, Plus, X, Info, Search, BarChart3, Table, ArrowUpDown, ArrowUp, ArrowDown, Loader, Edit2, Trash2, Check, XCircle, FileCheck } from 'lucide-react';
import FloatingButton from '../common/FloatingButton';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { useSidebar } from '../../context/SidebarContext';
import { fetchMathConcepts as fetchMathConceptsUtil, getConceptOrder as getConceptOrderUtil, getConceptById as getConceptByIdUtil } from '../../utils/mathConceptsUtils';
import { Modal, Button, MathceptDetailModal, useMathceptDetail } from '../common';
import SchoolSearchInput from './SchoolSearchInput';
import SchoolNameUtils from '../../utils/schoolNameUtils';
import schoolList from '../../data/school_list.json';

// concept_type에 따른 색상 매핑 (무지개색 50% 투명도)
const getConceptTypeColor = (type) => {
  const colors = {
    'DEF': 'rgba(239, 68, 68, 0.5)',   // 빨강 (정의)
    'AXM': 'rgba(249, 115, 22, 0.5)',  // 주황 (공리)
    'IDN': 'rgba(245, 158, 11, 0.5)',  // 노랑 (정리)
    'THM': 'rgba(34, 197, 94, 0.5)',   // 초록 (이론)
    'MTH': 'rgba(6, 182, 212, 0.5)',   // 하늘 (방법론)
    'PRP': 'rgba(59, 130, 246, 0.5)',  // 파랑 (성질)
    'FOR': 'rgba(139, 92, 246, 0.5)',  // 보라 (공식)
    'NTN': 'rgba(236, 72, 153, 0.5)'   // 분홍 (표기법)
  };
  return colors[type] || 'rgba(156, 163, 175, 0.5)'; // 기본 회색
};

// concept_type 한글 이름 매핑
const getConceptTypeName = (type) => {
  const names = {
    'DEF': '정의',
    'AXM': '공리',
    'IDN': '정리',
    'THM': '이론',
    'MTH': '방법론',
    'PRP': '성질',
    'FOR': '공식',
    'NTN': '표기법'
  };
  return names[type] || type;
};

// Virtual Scrolling 훅
const useVirtualScrolling = ({ 
  items, 
  itemHeight, 
  containerHeight, 
  overscan = 5 
}) => {
  const [scrollTop, setScrollTop] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef(null);

  // 가시 영역 계산
  const visibleRange = useMemo(() => {
    const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
    const endIndex = Math.min(
      items.length - 1,
      Math.ceil((scrollTop + containerHeight) / itemHeight) + overscan
    );
    
    return { startIndex, endIndex };
  }, [scrollTop, itemHeight, containerHeight, items.length, overscan]);

  // 가시 아이템들
  const visibleItems = useMemo(() => {
    const { startIndex, endIndex } = visibleRange;
    return items.slice(startIndex, endIndex + 1).map((item, index) => ({
      ...item,
      index: startIndex + index,
      originalIndex: startIndex + index
    }));
  }, [items, visibleRange]);

  // 스크롤 핸들러
  const handleScroll = useCallback((e) => {
    const newScrollTop = e.target.scrollTop;
    setScrollTop(newScrollTop);
    
    if (!isScrolling) {
      setIsScrolling(true);
    }

    // 스크롤 종료 감지
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 100); // 더 빠른 스크롤 종료 감지
  }, [isScrolling]);

  // 전체 높이와 오프셋 계산
  const totalHeight = items.length * itemHeight;
  const offsetY = visibleRange.startIndex * itemHeight;

  return {
    visibleItems,
    totalHeight,
    offsetY,
    handleScroll,
    isScrolling,
    visibleRange
  };
};

// 개별 셀 컴포넌트 (차트모드와 동일한 디자인)
const TableCell = React.memo(({ conceptId, columnId, columnIndex, value, maxValue }) => {
  // 값이 문자열인 경우 숫자 부분만 추출
  const numericValue = typeof value === 'string' ? parseFloat(value.replace('%', '')) || 0 : value;
  const displayValue = value; // 표시용 값은 원본 유지
  
  // 막대그래프 색상 (차트모드와 동일) - 20가지 이상의 구분 쉬운 색상
  const columnColors = [
    'from-red-500 to-red-400',           // 빨강
    'from-blue-500 to-blue-400',         // 파랑
    'from-green-500 to-green-400',       // 초록
    'from-purple-500 to-purple-400',     // 보라
    'from-orange-500 to-orange-400',     // 주황
    'from-pink-500 to-pink-400',         // 분홍
    'from-yellow-500 to-yellow-400',     // 노랑
    'from-cyan-500 to-cyan-400',         // 하늘
    'from-indigo-500 to-indigo-400',     // 남색
    'from-teal-500 to-teal-400',         // 청록
    'from-lime-500 to-lime-400',         // 라임
    'from-amber-500 to-amber-400',       // 호박색
    'from-emerald-500 to-emerald-400',   // 에메랄드
    'from-violet-500 to-violet-400',     // 바이올렛
    'from-rose-500 to-rose-400',         // 장미색
    'from-fuchsia-500 to-fuchsia-400',   // 자홍색
    'from-sky-500 to-sky-400',           // 하늘색
    'from-slate-500 to-slate-400',       // 슬레이트
    'from-zinc-500 to-zinc-400',         // 아연색
    'from-red-600 to-red-500',           // 진한 빨강
    'from-blue-600 to-blue-500',         // 진한 파랑
    'from-green-600 to-green-500',       // 진한 초록
    'from-purple-600 to-purple-500',     // 진한 보라
    'from-orange-600 to-orange-500',     // 진한 주황
    'from-pink-600 to-pink-500',         // 진한 분홍
  ];
  
  // 차트모드와 동일한 색상 계산 - columnIndex 사용
  const colorIndex = columnIndex % columnColors.length;
  
  // 막대 너비 계산 (차트모드와 동일)
  const percentage = maxValue > 0 ? (numericValue / maxValue) * 100 : 0;
  const barWidth = Math.max(percentage, numericValue > 0 ? 1 : 0);

  const cellStyle = useMemo(() => ({
    minWidth: '120px',
    width: '120px',
    maxWidth: '120px',
    padding: '4px',
    backgroundColor: '#ffffff',
    borderLeft: '1px solid #d1d5db'
  }), []);
  
  return (
    <td 
      className="text-center"
      title={`개념 ${conceptId}: ${displayValue} 출현`}
      style={cellStyle}
    >
      <div className="flex-1 relative flex items-center">
        {numericValue > 0 ? (
          <>
            <div 
              className={`bg-gradient-to-r ${columnColors[colorIndex]} rounded-sm`}
              style={{ 
                width: `${barWidth}%`,
                height: '10px', // 차트모드와 동일한 높이
                contain: 'layout style paint'
              }}
            />
            <span 
              className="font-semibold text-gray-700 ml-1"
              style={{ 
                fontSize: '9px', 
                lineHeight: '1',
                fontWeight: '600'
              }}
            >
              {displayValue}
            </span>
          </>
        ) : (
          // 차트 모드와 완전히 동일한 점선 세로 줄로 표시
          <div className="flex items-center">
            <div 
              className="border-l-2 border-dashed"
              style={{ 
                height: '8px', // 차트모드와 동일한 높이
                borderColor: (() => {
                  const colorClass = columnColors[colorIndex];
                  if (colorClass.includes('red')) return '#ef4444';
                  if (colorClass.includes('blue')) return '#3b82f6';
                  if (colorClass.includes('green')) return '#10b981';
                  if (colorClass.includes('purple')) return '#8b5cf6';
                  if (colorClass.includes('orange')) return '#f59e0b';
                  if (colorClass.includes('pink')) return '#ec4899';
                  if (colorClass.includes('yellow')) return '#eab308';
                  if (colorClass.includes('cyan')) return '#06b6d4';
                  if (colorClass.includes('indigo')) return '#6366f1';
                  if (colorClass.includes('teal')) return '#14b8a6';
                  if (colorClass.includes('lime')) return '#84cc16';
                  if (colorClass.includes('amber')) return '#f59e0b';
                  if (colorClass.includes('emerald')) return '#10b981';
                  if (colorClass.includes('violet')) return '#8b5cf6';
                  if (colorClass.includes('rose')) return '#f43f5e';
                  if (colorClass.includes('fuchsia')) return '#d946ef';
                  if (colorClass.includes('sky')) return '#0ea5e9';
                  if (colorClass.includes('slate')) return '#64748b';
                  if (colorClass.includes('zinc')) return '#71717a';
                  return '#6b7280'; // 기본 회색
                })()
              }}
            />
            <span 
              className="ml-2 text-gray-500 font-medium"
              style={{ fontSize: '8px' }} // 차트모드와 동일한 폰트 크기
            >
              0
            </span>
          </div>
        )}
      </div>
    </td>
  );
}, (prevProps, nextProps) => {
  // 커스텀 비교 함수로 정확한 렌더링 제어
  return prevProps.value === nextProps.value && 
         prevProps.maxValue === nextProps.maxValue &&
         prevProps.conceptId === nextProps.conceptId &&
         prevProps.columnId === nextProps.columnId &&
         prevProps.columnIndex === nextProps.columnIndex;
});

TableCell.displayName = 'TableCell';

// 테이블 행 컴포넌트 (최적화된 메모화)
const TableRow = React.memo(({ concept, index, columns, getCellValue, renderLatex, isMobileView, maxValue, isScrolling, getConceptById, onOrderClick }) => {
  const [isVisible, setIsVisible] = useState(false);
  const rowRef = useRef(null);

  // LaTeX 렌더링은 화면에 보일 때만 수행
  const renderedText = useMemo(() => {
    return isVisible ? renderLatex(concept.text) : concept.text;
  }, [concept.text, renderLatex, isVisible]);

  // Intersection Observer로 화면에 보이는지 감지
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1, rootMargin: '50px 0px' }
    );

    if (rowRef.current) {
      observer.observe(rowRef.current);
    }

    return () => {
      if (rowRef.current) {
        observer.unobserve(rowRef.current);
      }
    };
  }, []);
  
  return (
    <tr 
      ref={rowRef}
      className={`${index % 2 === 0 ? 'bg-gray-50' : ''} hover:bg-gray-50`}
      style={{ 
        contain: 'layout style paint',
        willChange: isScrolling ? 'transform' : 'auto',
        minHeight: `${Math.max(16, columns.length * 6 + 12)}px`,
        padding: '2px 0'
      }}
    >
      {/* 1열: 개념 순서 (툴팁 제거) */}
      <td className={`border-r border-gray-300 text-center font-medium ${isMobileView ? 'text-xs' : 'text-xs'}`} 
          style={{ 
            width: '80px',
            minWidth: '80px',
            maxWidth: '80px',
            backgroundColor: index % 2 === 0 ? '#f9fafb' : '#ffffff',
            zIndex: 25,
            padding: '2px'
          }}>
        <div className="flex items-center justify-center gap-1">
          {/* Order 뱃지 */}
          <span 
            className="inline-flex items-center justify-center font-semibold text-gray-700 cursor-pointer hover:bg-gray-400"
            style={{
              backgroundColor: 'rgba(156, 163, 175, 0.3)',
              borderRadius: '6px',
              width: '28px',
              height: '18px',
              minWidth: '28px',
              fontSize: '10px',
              transition: 'background-color 0.2s'
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (onOrderClick) onOrderClick(concept.id);
            }}
            title="클릭하여 상세 정보 보기"
          >
            {concept.order || concept.id}
          </span>
          
          {/* Concept Type 뱃지 */}
          {(() => {
            const conceptData = getConceptById ? getConceptById(concept.id) : null;
            return conceptData && conceptData.concept_type ? (
              <span 
                className="inline-flex items-center justify-center font-semibold text-gray-800"
                style={{
                  backgroundColor: getConceptTypeColor(conceptData.concept_type),
                  borderRadius: '6px',
                  width: '28px',
                  height: '18px',
                  minWidth: '28px',
                  fontSize: '9px'
                }}
                title={getConceptTypeName(conceptData.concept_type)}
              >
                {conceptData.concept_type}
              </span>
            ) : null;
          })()}
        </div>
      </td>
      
      {/* 2열: 수학 개념 (일반 컬럼, 고정 너비) */}
      <td className={`border-r border-gray-200 ${isMobileView ? 'text-xs' : 'text-xs'}`} 
          style={{ 
            width: '60%',
            padding: '1px 4px',
            overflow: 'hidden'
          }}>
        <div 
          title={concept.text.replace(/<[^>]*>/g, '')}
          dangerouslySetInnerHTML={{ __html: renderedText }}
          style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}
        />
      </td>
      
      {/* 데이터 컬럼들 */}
      {columns.map((column, columnIndex) => {
        const cellValue = getCellValue(concept.id, column.id);
        return (
          <TableCell
            key={column.id}
            conceptId={concept.id}
            columnId={column.id}
            columnIndex={columnIndex}
            value={cellValue}
            maxValue={maxValue}
          />
        );
      })}
    </tr>
  );
});

TableRow.displayName = 'TableRow';

// 차트 뷰 컴포넌트 (Virtual Scrolling 적용)
const ChartView = React.memo(({ 
  chartData, 
  chartMaxValue, 
  renderLatex, 
  isMobileView, 
  columns, 
  sortConfig, 
  handleColumnSort,
  containerHeight,
  mathConceptWidth,
  shouldTruncate,
  getConceptById,
  onOrderClick,
  schoolSummaryData,
  SchoolNameUtils,
  schoolList,
  setShowSchoolSummaryModal
}) => {
  console.log('ChartView 렌더링:', { 
    chartDataLength: chartData.length, 
    chartMaxValue, 
    sampleItems: chartData.slice(0, 3),
    columnsCount: columns.length
  });

  // 각 컬럼별 색상 정의 (20가지 이상의 구분 쉬운 색상)
  const columnColors = [
    'from-red-500 to-red-400',           // 빨강
    'from-blue-500 to-blue-400',         // 파랑
    'from-green-500 to-green-400',       // 초록
    'from-purple-500 to-purple-400',     // 보라
    'from-orange-500 to-orange-400',     // 주황
    'from-pink-500 to-pink-400',         // 분홍
    'from-yellow-500 to-yellow-400',     // 노랑
    'from-cyan-500 to-cyan-400',         // 하늘
    'from-indigo-500 to-indigo-400',     // 남색
    'from-teal-500 to-teal-400',         // 청록
    'from-lime-500 to-lime-400',         // 라임
    'from-amber-500 to-amber-400',       // 호박색
    'from-emerald-500 to-emerald-400',   // 에메랄드
    'from-violet-500 to-violet-400',     // 바이올렛
    'from-rose-500 to-rose-400',         // 장미색
    'from-fuchsia-500 to-fuchsia-400',   // 자홍색
    'from-sky-500 to-sky-400',           // 하늘색
    'from-slate-500 to-slate-400',       // 슬레이트
    'from-zinc-500 to-zinc-400',         // 아연색
    'from-red-600 to-red-500',           // 진한 빨강
    'from-blue-600 to-blue-500',         // 진한 파랑
    'from-green-600 to-green-500',       // 진한 초록
    'from-purple-600 to-purple-500',     // 진한 보라
    'from-orange-600 to-orange-500',     // 진한 주황
    'from-pink-600 to-pink-500',         // 진한 분홍
  ];

  // 차트 행 높이 계산
  const chartItemHeight = useMemo(() => {
    // 동적 행 높이 계산 (막대그래프 수에 따라)
    const baseHeight = 24; // 기본 최소 높이
    const barHeight = 12; // 각 막대그래프 높이
    const barSpacing = 1; // 막대그래프 간격
    const totalBarsHeight = columns.length * (barHeight + barSpacing);
    return Math.max(baseHeight, totalBarsHeight + 8); // 패딩 포함
  }, [columns.length]);

  // Virtual Scrolling 적용
  const {
    visibleItems,
    totalHeight,
    offsetY,
    handleScroll,
    isScrolling
  } = useVirtualScrolling({
    items: chartData,
    itemHeight: chartItemHeight,
    containerHeight: containerHeight - 80, // 헤더 높이 제외
    overscan: 8
  });

  return (
    <div className="flex flex-col h-full">
      {/* 컬럼 헤더 */}
      {columns.length > 0 && (
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 pb-1 mb-1 shrink-0">
          <div className="space-y-1">
            {/* 컬럼 범례 */}
            <div className="space-y-2">
              {/* 컬럼 정렬 버튼들 */}
              <div className="flex flex-wrap gap-2 justify-center">
                
                {columns.map((column, index) => {
                  const isActiveSortColumn = sortConfig.columnId === column.id;
                  const getSortIcon = () => {
                    if (!isActiveSortColumn) return <ArrowUpDown className="size-2 text-gray-400" />;
                    if (sortConfig.direction === 'desc') return <ArrowDown className="size-2 text-blue-600" />;
                    if (sortConfig.direction === 'asc') return <ArrowUp className="size-2 text-blue-600" />;
                    return <ArrowUpDown className="size-2 text-gray-400" />;
                  };
                  
                  return (
                    <div key={column.id} className="flex items-center gap-1">
                      <div 
                        className={`w-3 h-2 rounded-sm bg-gradient-to-r ${columnColors[index % columnColors.length]}`}
                      />
                      <button
                        onClick={() => handleColumnSort(column.id)}
                        className="flex items-center gap-1 hover:text-blue-600 transition-colors"
                        title={`${column.name} 정렬`}
                      >
                        <span className="text-xs text-gray-700" style={{ fontSize: '9px' }}>
                          {column.name.length > 10 ? column.name.substring(0, 10) + '...' : column.name}
                        </span>
                        {getSortIcon()}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* 가상화된 차트 데이터 */}
      <div 
        className="flex-1 overflow-auto"
        style={{ height: containerHeight - 80 }}
        onScroll={handleScroll}
      >
        {/* 전체 높이를 위한 컨테이너 */}
        <div style={{ height: totalHeight, position: 'relative' }}>
          {/* 가시 영역 행들 */}
          <div style={{ transform: `translateY(${offsetY}px)` }}>
            {visibleItems.map((item) => (
              <VirtualizedChartRow
                key={item.id}
                item={item}
                index={item.originalIndex}
                columns={columns}
                columnColors={columnColors}
                chartMaxValue={chartMaxValue}
                renderLatex={renderLatex}
                isScrolling={isScrolling}
                itemHeight={chartItemHeight}
                sortConfig={sortConfig}
                mathConceptWidth={mathConceptWidth}
                shouldTruncate={shouldTruncate}
                getConceptById={getConceptById}
                onOrderClick={onOrderClick}
              />
            ))}
          </div>
        </div>
        
        {/* 빈 상태 */}
        {chartData.length === 0 && (
          <div className="py-8 text-center text-gray-500 text-sm">
            표시할 데이터가 없습니다.
          </div>
        )}
      </div>
    </div>
  );
});

// 차트 행 컴포넌트
const ChartRow = React.memo(({ item, index, columns, columnColors, chartMaxValue, renderLatex, getConceptById, onOrderClick }) => {
  return (
    <div 
      className="hover:bg-gray-50 border-b border-gray-100" 
      style={{ 
        minHeight: `${Math.max(16, columns.length * 6 + 12)}px`, 
        padding: '2px 0',
        contain: 'layout style paint'
      }}
    >
      <div className="flex items-stretch h-full">
        {/* 개념 뱃지들 - 2열로 분리 */}
        <div className="shrink-0 flex flex-col justify-center gap-1 px-1 border-r border-gray-200" style={{ width: '40px' }}>
          {/* Order 뱃지 */}
          <span 
            className="inline-flex items-center justify-center font-semibold text-gray-700 cursor-pointer hover:bg-gray-400"
            style={{
              backgroundColor: 'rgba(156, 163, 175, 0.3)',
              borderRadius: '6px',
              width: '28px',
              height: '18px',
              minWidth: '28px',
              fontSize: '10px',
              transition: 'background-color 0.2s'
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (onOrderClick) onOrderClick(item.id);
            }}
            title="클릭하여 상세 정보 보기"
          >
            {item.order || item.id}
          </span>
          
          {/* Concept Type 뱃지 */}
          {(() => {
            const conceptData = getConceptById ? getConceptById(item.id) : null;
            return conceptData && conceptData.concept_type ? (
              <span 
                className="inline-flex items-center justify-center font-semibold text-gray-800"
                style={{
                  backgroundColor: getConceptTypeColor(conceptData.concept_type),
                  borderRadius: '6px',
                  width: '28px',
                  height: '18px',
                  minWidth: '28px',
                  fontSize: '9px'
                }}
                title={getConceptTypeName(conceptData.concept_type)}
              >
                {conceptData.concept_type}
              </span>
            ) : null;
          })()}
        </div>
        
        {/* 수학 개념 */}
        <div className="shrink-0 px-1 text-xs overflow-hidden border-r border-gray-200" 
             style={{ width: 'calc(50% - 40px)', wordBreak: 'break-word' }}
             title={item.text.replace(/<[^>]*>/g, '')}>
          <div 
            className="text-xs"
            dangerouslySetInnerHTML={{ __html: renderLatex(item.text) }}
            style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
          />
        </div>
        
        {/* 각 컬럼별 막대그래프 - 50% 너비 */}
        <div className="flex-1 flex flex-col justify-center space-y-0" style={{ width: '50%' }}>
          {item.columns.map((columnData, colIndex) => {
            const percentage = chartMaxValue > 0 ? (columnData.value / chartMaxValue) * 100 : 0;
            const barWidth = Math.max(percentage, columnData.value > 0 ? 1 : 0);
            
            // 컬럼 수에 따른 막대그래프 높이 동적 조절 - 더 큰 높이, 겹치지 않도록
            const barRowHeight = Math.max(8, Math.min(14, 32 / Math.max(1, columns.length)));
            
            return (
              <div key={columnData.columnId} className="relative flex items-center" style={{ height: `${barRowHeight + 2}px`, marginBottom: '2px' }}>
                <div className="flex-1 relative flex items-center">
                  {columnData.value > 0 ? (
                    <>
                      <div 
                        className={`bg-gradient-to-r ${columnColors[colIndex % columnColors.length]} rounded-sm`}
                        style={{ 
                          width: `${barWidth}%`,
                          height: `${Math.max(8, barRowHeight)}px`,
                          contain: 'layout style paint'
                        }}
                      />
                      <span className="font-semibold text-gray-700 ml-1" style={{ fontSize: '9px', lineHeight: '1' }}>
                        {columnData.value}
                      </span>
                    </>
                  ) : (
                    // 데이터가 없을 때 - 아주 얇은 점선, 데이터 '1'보다 더 작게
                    <div className="flex items-center">
                      <div 
                        className={`rounded-sm border border-dashed`}
                        style={{ 
                          width: '12px',
                          height: `${Math.max(2, barRowHeight / 2)}px`,
                          borderColor: (() => {
                            const colorClass = columnColors[colIndex % columnColors.length];
                            if (colorClass.includes('red')) return '#ef4444';
                            if (colorClass.includes('blue')) return '#3b82f6';
                            if (colorClass.includes('green')) return '#10b981';
                            if (colorClass.includes('purple')) return '#8b5cf6';
                            if (colorClass.includes('orange')) return '#f59e0b';
                            if (colorClass.includes('pink')) return '#ec4899';
                            if (colorClass.includes('yellow')) return '#eab308';
                            if (colorClass.includes('cyan')) return '#06b6d4';
                            if (colorClass.includes('indigo')) return '#6366f1';
                            if (colorClass.includes('teal')) return '#14b8a6';
                            if (colorClass.includes('lime')) return '#84cc16';
                            if (colorClass.includes('amber')) return '#f59e0b';
                            if (colorClass.includes('emerald')) return '#10b981';
                            if (colorClass.includes('violet')) return '#8b5cf6';
                            if (colorClass.includes('rose')) return '#f43f5e';
                            if (colorClass.includes('fuchsia')) return '#d946ef';
                            if (colorClass.includes('sky')) return '#0ea5e9';
                            if (colorClass.includes('slate')) return '#64748b';
                            if (colorClass.includes('zinc')) return '#71717a';
                            return '#6b7280'; // 기본 회색
                          })(),
                          borderWidth: '1px'
                        }}
                      />
                      <span className="ml-1 text-gray-500 font-medium" style={{ fontSize: '8px' }}>0</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

ChartRow.displayName = 'ChartRow';
ChartView.displayName = 'ChartView';

// Virtual Scrolling을 적용한 테이블 컴포넌트
const VirtualizedTable = React.memo(({
  concepts,
  columns,
  getCellValue,
  renderLatex,
  isMobileView,
  maxValue,
  containerHeight,
  itemHeight,
  isLoadingConcepts,
  mathConceptsLength,
  mathConcepts,
  sortConfig,
  handleColumnSort,
  handleRemoveColumn,
  mathConceptWidth,
  shouldTruncate,
  getConceptById,
  onOrderClick,
  schoolSummaryData,
  SchoolNameUtils,
  schoolList
}) => {
  // Virtual Scrolling 적용
  const {
    visibleItems,
    totalHeight,
    offsetY,
    handleScroll,
    isScrolling
  } = useVirtualScrolling({
    items: concepts,
    itemHeight,
    containerHeight: containerHeight - 60, // 헤더 높이 제외
    overscan: 10 // 더 많은 오버스캔으로 스크롤 성능 향상
  });

  // 테이블 전체 너비 계산
  const tableWidth = useMemo(() => {
    // 1열: 80px (뱃지 열), 2열: mathConceptWidth, 데이터 컬럼들: 각 120px
    const firstColumnWidth = 80; // 뱃지 열
    const secondColumnWidth = parseFloat(mathConceptWidth) / 100 * window.innerWidth; // 수학 개념 열
    const dataColumnsWidth = columns.length * 120; // 데이터 컬럼들
    const calculatedWidth = firstColumnWidth + secondColumnWidth + dataColumnsWidth;
    return Math.max(500, calculatedWidth); // 여백 제거하여 정확한 너비 설정
  }, [mathConceptWidth, columns.length]);

  // 정렬 아이콘 생성 함수
  const getSortIcon = (columnId) => {
    const isActive = sortConfig.columnId === columnId;
    if (!isActive) return <ArrowUpDown className="size-3 text-gray-400" />;
    if (sortConfig.direction === 'desc') return <ArrowDown className="size-3 text-blue-600" />;
    if (sortConfig.direction === 'asc') return <ArrowUp className="size-3 text-blue-600" />;
    return <ArrowUpDown className="size-3 text-gray-400" />;
  };

  return (
    <div className="min-h-0 flex-1 rounded border border-gray-200 flex flex-col overflow-hidden">
      {/* 가상화된 테이블 바디 - 헤더와 데이터가 함께 스크롤 */}
      <div 
        className="flex-1 overflow-auto bg-white"
        style={{ height: containerHeight - 60 }}
        onScroll={handleScroll}
      >
        {/* 전체 높이를 위한 컨테이너 */}
        <div style={{ height: totalHeight, position: 'relative', width: tableWidth, minWidth: tableWidth }}>
          {/* 테이블 헤더 - sticky로 고정하되 횡스크롤은 함께 */}
          <div className="sticky top-0 z-30 bg-white border-b border-gray-200">
            
            <div 
              className="flex items-center"
              style={{ 
                width: tableWidth,
                minWidth: tableWidth
              }}
            >
              {/* No 컬럼 */}
              <div 
                className="bg-white text-center font-medium text-gray-500 text-xs border-r border-gray-300"
                style={{ width: '80px', minWidth: '80px', maxWidth: '80px', padding: '4px 2px' }}
              >
                No
              </div>
              
              {/* 수학 개념 컬럼 */}
              <div 
                className="bg-white text-left font-medium text-gray-500 text-xs border-r border-gray-300"
                style={{ width: mathConceptWidth, minWidth: mathConceptWidth, maxWidth: mathConceptWidth, padding: '4px 8px' }}
              >
                수학 개념
              </div>
              
              {/* 데이터 컬럼들 */}
              {columns.map(column => (
                <div 
                  key={column.id}
                  className="bg-white text-center font-medium text-gray-500 text-xs border-r border-gray-300"
                  style={{ minWidth: '120px', width: '120px', maxWidth: '120px', padding: '2px' }}
                >
                  <div className="flex items-center justify-center gap-1 py-1">
                    <button
                      onClick={() => handleColumnSort(column.id)}
                      className="flex items-center gap-1 hover:text-blue-600 transition-colors"
                      title={`${column.name} 정렬`}
                    >
                      <span className="text-xs" title={column.name}>
                        {column.name}
                      </span>
                      {getSortIcon(column.id)}
                    </button>
                    <button
                      onClick={() => handleRemoveColumn(column.id)}
                      className="text-red-400 hover:text-red-600"
                      title="컬럼 삭제"
                    >
                      <X className="size-3" />
                    </button>
                  </div>
                  <div className="flex items-center justify-center gap-1 text-xs text-gray-400">
                    <span>{column.sxmIds.length}개</span>
                    <div className="group/tooltip relative">
                      <Info className="size-2 cursor-help text-gray-400 hover:text-gray-600" />
                      <div className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 -translate-x-1/2 opacity-0 transition-opacity duration-300 group-hover/tooltip:opacity-100">
                        <div className="rounded-3xl border border-gray-700 bg-gray-900 px-4 py-3 text-xs text-white shadow-lg" style={{ minWidth: '200px', maxWidth: '300px' }}>
                          <div className="mb-2 font-semibold text-gray-200">📋 시험 ID 목록 ({column.sxmIds.length}개)</div>
                          <div className="max-h-24 overflow-y-auto leading-relaxed text-gray-300">
                            {column.sxmIds.length <= 10 
                              ? column.sxmIds.join(', ')
                              : `${column.sxmIds.slice(0, 10).join(', ')}... +${column.sxmIds.length - 10}개 더`
                            }
                          </div>
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2">
                            <div className="border-4 border-transparent border-b-gray-900"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* 가시 영역 행들 - 공백 완전 제거 */}
          <div style={{ transform: `translateY(${offsetY}px)` }}>
            {visibleItems.map((concept) => (
              <VirtualizedTableRow
                key={concept.id}
                concept={concept}
                index={concept.originalIndex}
                columns={columns}
                getCellValue={getCellValue}
                renderLatex={renderLatex}
                isMobileView={isMobileView}
                maxValue={maxValue}
                isScrolling={isScrolling}
                itemHeight={itemHeight}
                sortConfig={sortConfig}
                mathConceptWidth={mathConceptWidth}
                shouldTruncate={shouldTruncate}
                tableWidth={tableWidth}
                getConceptById={getConceptById}
                onOrderClick={onOrderClick}
              />
            ))}
          </div>
        </div>
        
        {/* 빈 상태 */}
        {concepts.length === 0 && !isLoadingConcepts && (
          <div className="py-12 text-center text-gray-500">
            {mathConceptsLength === 0 ? '데이터가 없습니다.' : '표시할 데이터가 없습니다.'}
          </div>
        )}
      </div>
    </div>
  );
});

VirtualizedTable.displayName = 'VirtualizedTable';

// Virtual Scrolling을 위한 최적화된 테이블 행
const VirtualizedTableRow = React.memo(({ 
  concept, 
  index, 
  columns, 
  getCellValue, 
  renderLatex, 
  isMobileView, 
  maxValue, 
  isScrolling,
  itemHeight,
  sortConfig,
  mathConceptWidth,
  shouldTruncate,
  tableWidth,
  getConceptById,
  onOrderClick 
}) => {
  // LaTeX 렌더링 (스크롤 중에도 간단한 수식은 렌더링)
  const renderedText = useMemo(() => {
    if (isScrolling && concept.text.includes('$')) {
      // 스크롤 중에는 복잡한 수식만 스킵, 간단한 텍스트는 렌더링
      return concept.text.split('$').length > 4 ? concept.text : renderLatex(concept.text);
    }
    return renderLatex(concept.text);
  }, [concept.text, renderLatex, isScrolling]);

  return (
    <div 
      className="bg-white hover:bg-gray-50 flex items-stretch border-b border-gray-100"
      style={{ 
        minHeight: itemHeight,
        width: tableWidth,
        minWidth: tableWidth,
        contain: 'layout style paint',
        willChange: isScrolling ? 'transform' : 'auto'
      }}
    >
      {/* 1열: 개념 뱃지들 */}
      <div 
        className="border-r border-gray-300"
        style={{ 
          width: '80px',
          minWidth: '80px',
          maxWidth: '80px',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2px',
          gap: '2px'
        }}
      >
        {/* Order 뱃지 */}
        <span 
          className="inline-flex items-center justify-center font-semibold text-gray-700 cursor-pointer hover:bg-gray-400"
          style={{
            backgroundColor: 'rgba(156, 163, 175, 0.3)',
            borderRadius: '6px',
            width: '28px',
            height: '18px',
            minWidth: '28px',
            fontSize: '10px',
            transition: 'background-color 0.2s'
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (onOrderClick) onOrderClick(concept.id);
          }}
          title="클릭하여 상세 정보 보기"
        >
          {concept.order || concept.id}
        </span>
        
        {/* Concept Type 뱃지 */}
        {(() => {
          const conceptData = getConceptById ? getConceptById(concept.id) : null;
          return conceptData && conceptData.concept_type ? (
            <span 
              className="inline-flex items-center justify-center font-semibold text-gray-800"
              style={{
                backgroundColor: getConceptTypeColor(conceptData.concept_type),
                borderRadius: '6px',
                width: '28px',
                height: '18px',
                minWidth: '28px',
                fontSize: '9px'
              }}
              title={getConceptTypeName(conceptData.concept_type)}
            >
              {conceptData.concept_type}
            </span>
          ) : null;
        })()}
      </div>
      
      {/* 2열: 수학 개념 */}
      <div 
        className="text-xs border-r border-gray-300"
        style={{ 
          width: mathConceptWidth,
          minWidth: mathConceptWidth,
          maxWidth: mathConceptWidth,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          padding: '2px 8px'
        }}
        title={concept.text.replace(/<[^>]*>/g, '')}
      >
        <div 
          className={`${shouldTruncate || concept.text.replace(/<[^>]*>/g, '').length > 60 ? 'truncate' : 'break-words'}`}
          dangerouslySetInnerHTML={{ __html: renderedText }}
          style={{ wordBreak: shouldTruncate || concept.text.replace(/<[^>]*>/g, '').length > 60 ? 'normal' : 'break-word', whiteSpace: shouldTruncate || concept.text.replace(/<[^>]*>/g, '').length > 60 ? 'nowrap' : 'normal' }}
        />
      </div>
      
      {/* 데이터 컬럼들 */}
      {columns.map((column, columnIndex) => {
        const cellValue = getCellValue(concept.id, column.id);
        return (
          <VirtualizedTableCell
            key={column.id}
            conceptId={concept.id}
            columnId={column.id}
            columnIndex={columnIndex}
            value={cellValue}
            maxValue={maxValue}
            itemHeight={itemHeight}
            sortConfig={sortConfig}
          />
        );
      })}
    </div>
  );
}, (prevProps, nextProps) => {
  // 성능 최적화를 위한 커스텀 비교
  return prevProps.concept.id === nextProps.concept.id &&
         prevProps.index === nextProps.index &&
         prevProps.maxValue === nextProps.maxValue &&
         prevProps.isScrolling === nextProps.isScrolling &&
         prevProps.columns.length === nextProps.columns.length &&
         prevProps.tableWidth === nextProps.tableWidth;
});

VirtualizedTableRow.displayName = 'VirtualizedTableRow';

// Virtual Scrolling을 위한 최적화된 테이블 셀 (차트모드와 동일한 디자인)
const VirtualizedTableCell = React.memo(({ 
  conceptId, 
  columnId,
  columnIndex, 
  value, 
  maxValue,
  itemHeight,
  sortConfig 
}) => {
  // 값이 문자열인 경우 숫자 부분만 추출
  const numericValue = typeof value === 'string' ? parseFloat(value.replace('%', '')) || 0 : value;
  const displayValue = value; // 표시용 값은 원본 유지
  
  // 막대그래프 색상 (차트모드와 동일) - 20가지 이상의 구분 쉬운 색상
  const columnColors = [
    'from-red-500 to-red-400',           // 빨강
    'from-blue-500 to-blue-400',         // 파랑
    'from-green-500 to-green-400',       // 초록
    'from-purple-500 to-purple-400',     // 보라
    'from-orange-500 to-orange-400',     // 주황
    'from-pink-500 to-pink-400',         // 분홍
    'from-yellow-500 to-yellow-400',     // 노랑
    'from-cyan-500 to-cyan-400',         // 하늘
    'from-indigo-500 to-indigo-400',     // 남색
    'from-teal-500 to-teal-400',         // 청록
    'from-lime-500 to-lime-400',         // 라임
    'from-amber-500 to-amber-400',       // 호박색
    'from-emerald-500 to-emerald-400',   // 에메랄드
    'from-violet-500 to-violet-400',     // 바이올렛
    'from-rose-500 to-rose-400',         // 장미색
    'from-fuchsia-500 to-fuchsia-400',   // 자홍색
    'from-sky-500 to-sky-400',           // 하늘색
    'from-slate-500 to-slate-400',       // 슬레이트
    'from-zinc-500 to-zinc-400',         // 아연색
    'from-red-600 to-red-500',           // 진한 빨강
    'from-blue-600 to-blue-500',         // 진한 파랑
    'from-green-600 to-green-500',       // 진한 초록
    'from-purple-600 to-purple-500',     // 진한 보라
    'from-orange-600 to-orange-500',     // 진한 주황
    'from-pink-600 to-pink-500',         // 진한 분홍
  ];
  
  // 차트모드와 동일한 색상 계산 - columnIndex 사용
  const colorIndex = columnIndex % columnColors.length;
  const isActiveSortColumn = sortConfig.columnId === columnId;
  
  // 막대 너비 계산 (차트모드와 동일)
  const percentage = maxValue > 0 ? (numericValue / maxValue) * 100 : 0;
  const barWidth = Math.max(percentage, numericValue > 0 ? 1 : 0);
  
  const cellStyle = useMemo(() => ({
    width: '120px',
    minWidth: '120px',
    maxWidth: '120px',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '4px',
    backgroundColor: '#ffffff',
    borderLeft: '1px solid #d1d5db'
  }), []);
  
  return (
    <div 
      className="border-l border-gray-300"
      style={cellStyle}
      title={`개념 ${conceptId}: ${displayValue} 출현`}
    >
      <div className="flex-1 relative flex items-center">
        {numericValue > 0 ? (
          <>
            <div 
              className={`bg-gradient-to-r ${columnColors[colorIndex]} rounded-sm`}
              style={{ 
                width: `${barWidth}%`,
                height: '10px', // 차트모드와 동일한 높이
                contain: 'layout style paint'
              }}
            />
            <span 
              className={`text-gray-700 ml-1 ${
                isActiveSortColumn 
                  ? 'font-black text-blue-800 bg-blue-100 px-1 py-0.5 rounded-sm shadow-sm' 
                  : 'font-semibold'
              }`}
              style={{ 
                fontSize: '9px', 
                lineHeight: '1',
                fontWeight: isActiveSortColumn ? '900' : '600' // 차트모드와 동일한 강조
              }}
            >
              {displayValue}
            </span>
          </>
        ) : (
          // 차트 모드와 완전히 동일한 점선 세로 줄로 표시
          <div className="flex items-center">
            <div 
              className="border-l-2 border-dashed"
              style={{ 
                height: '8px', // 차트모드와 동일한 높이
                borderColor: (() => {
                  const colorClass = columnColors[colorIndex];
                  if (colorClass.includes('red')) return '#ef4444';
                  if (colorClass.includes('blue')) return '#3b82f6';
                  if (colorClass.includes('green')) return '#10b981';
                  if (colorClass.includes('purple')) return '#8b5cf6';
                  if (colorClass.includes('orange')) return '#f59e0b';
                  if (colorClass.includes('pink')) return '#ec4899';
                  if (colorClass.includes('yellow')) return '#eab308';
                  if (colorClass.includes('cyan')) return '#06b6d4';
                  if (colorClass.includes('indigo')) return '#6366f1';
                  if (colorClass.includes('teal')) return '#14b8a6';
                  if (colorClass.includes('lime')) return '#84cc16';
                  if (colorClass.includes('amber')) return '#f59e0b';
                  if (colorClass.includes('emerald')) return '#10b981';
                  if (colorClass.includes('violet')) return '#8b5cf6';
                  if (colorClass.includes('rose')) return '#f43f5e';
                  if (colorClass.includes('fuchsia')) return '#d946ef';
                  if (colorClass.includes('sky')) return '#0ea5e9';
                  if (colorClass.includes('slate')) return '#64748b';
                  if (colorClass.includes('zinc')) return '#71717a';
                  return '#6b7280'; // 기본 회색
                })()
              }}
            />
            <span 
              className={`ml-2 text-gray-500 font-medium ${
                isActiveSortColumn 
                  ? 'bg-blue-100 px-1 py-0.5 rounded-sm shadow-sm text-blue-700' 
                  : ''
              }`} 
                             style={{ fontSize: '8px' }} // 차트모드와 동일한 폰트 크기
            >
              0
            </span>
          </div>
        )}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  return prevProps.value === nextProps.value && 
         prevProps.maxValue === nextProps.maxValue &&
         prevProps.sortConfig.columnId === nextProps.sortConfig.columnId &&
         prevProps.columnIndex === nextProps.columnIndex;
});

VirtualizedTableCell.displayName = 'VirtualizedTableCell';

// Virtual Scrolling을 위한 최적화된 차트 행
const VirtualizedChartRow = React.memo(({ 
  item, 
  index, 
  columns, 
  columnColors, 
  chartMaxValue, 
  renderLatex,
  isScrolling,
  itemHeight,
  sortConfig,
  mathConceptWidth,
  shouldTruncate,
  getConceptById,
  onOrderClick 
}) => {
  // LaTeX 렌더링 (스크롤 중에도 간단한 수식은 렌더링)
  const renderedText = useMemo(() => {
    if (isScrolling && item.text.includes('$')) {
      // 스크롤 중에는 복잡한 수식만 스킵, 간단한 텍스트는 렌더링
      return item.text.split('$').length > 4 ? item.text : renderLatex(item.text);
    }
    return renderLatex(item.text);
  }, [item.text, renderLatex, isScrolling]);

  // 동적 행 높이 계산 (막대그래프 수에 따라)
  const dynamicHeight = useMemo(() => {
    const baseHeight = 24; // 기본 최소 높이
    const barHeight = 12; // 각 막대그래프 높이
    const barSpacing = 1; // 막대그래프 간격
    const totalBarsHeight = columns.length * (barHeight + barSpacing);
    return Math.max(baseHeight, totalBarsHeight + 8); // 패딩 포함
  }, [columns.length]);

  return (
    <div 
      className="hover:bg-gray-50 border-b border-gray-100" 
      style={{ 
        minHeight: `${dynamicHeight}px`,
        padding: '4px 0',
        contain: 'layout style paint'
      }}
      title={`개념 ${item.order || item.id}: ${item.text.replace(/<[^>]*>/g, '')} (총 ${item.total}회)`}
    >
      <div className="flex items-stretch h-full">
        {/* 개념 뱃지들 - 2열로 분리 */}
        <div className="shrink-0 flex flex-col justify-center gap-1 border-r border-gray-200"
             style={{ width: '40px', minWidth: '40px', maxWidth: '40px', padding: '0 2px' }}>
          {/* Order 뱃지 */}
          <span 
            className="inline-flex items-center justify-center font-semibold text-gray-700 cursor-pointer hover:bg-gray-400"
            style={{
              backgroundColor: 'rgba(156, 163, 175, 0.3)',
              borderRadius: '6px',
              width: '28px',
              height: '18px',
              minWidth: '28px',
              fontSize: '10px',
              transition: 'background-color 0.2s'
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (onOrderClick) onOrderClick(item.id);
            }}
            title="클릭하여 상세 정보 보기"
          >
            {item.order || item.id}
          </span>
          
          {/* Concept Type 뱃지 */}
          {(() => {
            const conceptData = getConceptById ? getConceptById(item.id) : null;
            return conceptData && conceptData.concept_type ? (
              <span 
                className="inline-flex items-center justify-center font-semibold text-gray-800"
                style={{
                  backgroundColor: getConceptTypeColor(conceptData.concept_type),
                  borderRadius: '6px',
                  width: '28px',
                  height: '18px',
                  minWidth: '28px',
                  fontSize: '9px'
                }}
                title={getConceptTypeName(conceptData.concept_type)}
              >
                {conceptData.concept_type}
              </span>
            ) : null;
          })()}
        </div>
        
        {/* 수학 개념 - 50% 고정 */}
        <div className="shrink-0 border-r border-gray-200 flex items-center overflow-hidden" 
             style={{ width: 'calc(50% - 40px)', minWidth: 'calc(50% - 40px)', maxWidth: 'calc(50% - 40px)', padding: '0 8px' }}>
          <div 
            className="text-sm break-words"
            dangerouslySetInnerHTML={{ __html: renderedText }}
            title={item.text.replace(/<[^>]*>/g, '')}
            style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}
          />
        </div>
        
        {/* 각 컬럼별 막대그래프 - 50% 고정 */}
        <div className="flex flex-col justify-center px-2" style={{ gap: '1px', width: '50%', minWidth: '50%', maxWidth: '50%' }}>
          {item.columns.map((columnData, colIndex) => {
            // 값이 문자열인 경우 숫자 부분만 추출
            const numericValue = typeof columnData.value === 'string' ? parseFloat(columnData.value.replace('%', '')) || 0 : columnData.value;
            const displayValue = columnData.value; // 표시용 값은 원본 유지
            const percentage = chartMaxValue > 0 ? (numericValue / chartMaxValue) * 100 : 0;
            const barWidth = Math.max(percentage, numericValue > 0 ? 1 : 0);
            const isActiveSortColumn = sortConfig.columnId === columnData.columnId;
            
            return (
              <div key={columnData.columnId} className="relative flex items-center" style={{ height: '12px' }}>
                <div className="flex-1 relative flex items-center">
                  {numericValue > 0 ? (
                    <>
                      <div 
                        className={`bg-gradient-to-r ${columnColors[colIndex % columnColors.length]} rounded-sm`}
                        style={{ 
                          width: `${barWidth}%`,
                          height: '10px',
                          contain: 'layout style paint'
                        }}
                      />
                      <span 
                        className={`text-gray-700 ml-1 ${
                          isActiveSortColumn 
                            ? 'font-black text-blue-800 bg-blue-100 px-1 py-0.5 rounded-sm shadow-sm' 
                            : 'font-semibold'
                        }`} 
                        style={{ 
                          fontSize: '9px', 
                          lineHeight: '1',
                          fontWeight: isActiveSortColumn ? '900' : '600' // 더 진한 볼드
                        }}
                      >
                        {displayValue}
                      </span>
                    </>
                  ) : (
                    // 빈 데이터는 점선 세로 줄로 표시
                    <div className="flex items-center">
                      <div 
                        className="border-l-2 border-dashed"
                        style={{ 
                          height: '8px',
                          borderColor: (() => {
                            const colorClass = columnColors[colIndex % columnColors.length];
                            if (colorClass.includes('red')) return '#ef4444';
                            if (colorClass.includes('blue')) return '#3b82f6';
                            if (colorClass.includes('green')) return '#10b981';
                            if (colorClass.includes('purple')) return '#8b5cf6';
                            if (colorClass.includes('orange')) return '#f59e0b';
                            if (colorClass.includes('pink')) return '#ec4899';
                            if (colorClass.includes('yellow')) return '#eab308';
                            if (colorClass.includes('cyan')) return '#06b6d4';
                            if (colorClass.includes('indigo')) return '#6366f1';
                            if (colorClass.includes('teal')) return '#14b8a6';
                            if (colorClass.includes('lime')) return '#84cc16';
                            if (colorClass.includes('amber')) return '#f59e0b';
                            if (colorClass.includes('emerald')) return '#10b981';
                            if (colorClass.includes('violet')) return '#8b5cf6';
                            if (colorClass.includes('rose')) return '#f43f5e';
                            if (colorClass.includes('fuchsia')) return '#d946ef';
                            if (colorClass.includes('sky')) return '#0ea5e9';
                            if (colorClass.includes('slate')) return '#64748b';
                            if (colorClass.includes('zinc')) return '#71717a';
                            return '#6b7280'; // 기본 회색
                          })()
                        }}
                      />
                      <span 
                        className={`ml-2 text-gray-500 font-medium ${
                          isActiveSortColumn 
                            ? 'bg-blue-100 px-1 py-0.5 rounded-sm shadow-sm text-blue-700' 
                            : ''
                        }`} 
                        style={{ fontSize: '8px' }}
                      >
                        {typeof displayValue === 'string' && displayValue.includes('%') ? '0%' : '0'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // 성능 최적화를 위한 커스텀 비교
  return prevProps.item.id === nextProps.item.id &&
         prevProps.index === nextProps.index &&
         prevProps.chartMaxValue === nextProps.chartMaxValue &&
         prevProps.isScrolling === nextProps.isScrolling &&
         prevProps.columns.length === nextProps.columns.length;
});

VirtualizedChartRow.displayName = 'VirtualizedChartRow';

function AnalysisResultTable() {
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isExpanded } = useSidebar(); // 사이드바 상태 가져오기
  const [mathConcepts, setMathConcepts] = useState([]);
  const [isLoadingMathConcepts, setIsLoadingMathConcepts] = useState(false);
  const [columns, setColumns] = useState([]);
  const [analysisData, setAnalysisData] = useState(new Map());
  const [hideEmptyRows, setHideEmptyRows] = useState(false);
  const [showAddColumnModal, setShowAddColumnModal] = useState(false);
  const [showSchoolSummaryModal, setShowSchoolSummaryModal] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConcepts, setIsLoadingConcepts] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isLoadingIds, setIsLoadingIds] = useState(false);
  const [error, setError] = useState(null);
  const [availableIds, setAvailableIds] = useState([]);
  const [examDetails, setExamDetails] = useState([]);
  
  // 프리셋 모달 관련 state
  const [modalTab, setModalTab] = useState('list'); // 'list' | 'add'
  const [presetList, setPresetList] = useState([]);
  const [presetSearchParams, setPresetSearchParams] = useState({
    page: 1,
    per_page: 20,
    author: '',
    sxmset_name: ''
  });
  const [presetTotalPages, setPresetTotalPages] = useState(1);
  const [presetTotalItems, setPresetTotalItems] = useState(0);
  const [isLoadingPresets, setIsLoadingPresets] = useState(false);
  const [editingPresetId, setEditingPresetId] = useState(null);
  const [editingPresetName, setEditingPresetName] = useState('');
  const [loadingPresetId, setLoadingPresetId] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'chart'
  const [dataScale, setDataScale] = useState('cnt'); // 'cnt', 'f', 'cov', 'p_beta', 'normal', 'rate'
  const [sortConfig, setSortConfig] = useState({ columnId: null, direction: 'original' }); // null, 'asc', 'desc', 'original'
  const [filters, setFilters] = useState({
    school_name: [],
    att_year: [],
    school_grade: [],
    phase: [],
    subject: ''
  });
  
  // 새 컬럼 추가 모달에서 사용할 학교명 검색 state
  const [newColumnSchoolSearch, setNewColumnSchoolSearch] = useState('');
  // 학교/문제집 토글 state
  const [searchInputType, setSearchInputType] = useState('school');
  
  // 매스컨셉 상세 정보 모달 (공통 컴포넌트 사용)
  const { isModalOpen: detailModalOpen, loading: detailLoading, data: detailData, fetchAndShow: fetchMathceptDetails, closeModal: closeDetailModal } = useMathceptDetail({ handleAuth: true, useBearerPrefix: true });
  
  // 알림 메시지 상태
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  // 모바일 뷰 감지
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  
  // Virtual Scrolling 설정
  const [containerHeight, setContainerHeight] = useState(600);
  const itemHeight = useMemo(() => {
    // 테이블 뷰용 고정 높이 (차트 모드와 독립적)
    return 32; // 간단하고 일정한 높이
  }, []);

  // 컨테이너 높이 동적 계산
  useEffect(() => {
    const updateContainerHeight = () => {
      const viewportHeight = window.innerHeight;
      const headerHeight = 50; // 헤더와 기타 UI 높이
      const availableHeight = viewportHeight - headerHeight;
      setContainerHeight(Math.max(400, availableHeight));
    };

    updateContainerHeight();
    window.addEventListener('resize', updateContainerHeight);
    return () => window.removeEventListener('resize', updateContainerHeight);
  }, []);

  // 2열 최적 너비 계산 (차트 뷰에서는 더 넓게)
  const mathConceptWidth = useMemo(() => {
    if (!mathConcepts.length) return viewMode === 'chart' ? '45%' : '40%';
    
    // 수학 개념들의 텍스트 길이 분석
    const maxTextLength = Math.max(...mathConcepts.map(concept => 
      concept.text.replace(/<[^>]*>/g, '').length
    ));
    
    // 기본 너비 비율 계산
    if (viewMode === 'chart') {
      // 차트 뷰: 첫 번째 열이 줄어들었으므로 두 번째 열을 더 넓게
      const baseWidth = Math.min(55, Math.max(30, Math.min(maxTextLength * 0.7, 55)));
      return `${baseWidth}%`;
    } else {
      // 테이블 뷰: 기존과 동일
      const baseWidth = Math.min(50, Math.max(25, Math.min(maxTextLength * 0.6, 50)));
      return `${baseWidth}%`;
    }
  }, [mathConcepts, viewMode]);

  const shouldTruncate = useMemo(() => {
    return parseFloat(mathConceptWidth) >= 45; // 45% 이상이면 truncate
  }, [mathConceptWidth]);
  
  // 디바운스 훅
  const useDebounce = (callback, delay) => {
    const timeoutRef = useRef(null);
    
    return useCallback((...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    }, [callback, delay]);
  };
  
  // 스크롤 성능 최적화
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef(null);
  
  const handleScrollEnd = useDebounce(() => {
    setIsScrolling(false);
  }, 100); // 더 빠른 스크롤 종료 감지
  
  const handleScroll = useCallback((e) => {
    if (!isScrolling) {
      setIsScrolling(true);
    }
    handleScrollEnd();
  }, [isScrolling, handleScrollEnd]);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 필터 옵션들
  // schools 배열은 이제 SchoolSearchInput을 통해 동적으로 선택됨

  const years = Array.from({ length: 10 }, (_, i) => 2027 - i);

  const grades = [
    { value: '1', label: '1학년 (초등)' },
    { value: '2', label: '2학년 (초등)' },
    { value: '3', label: '3학년 (초등)' },
    { value: '4', label: '4학년 (초등)' },
    { value: '5', label: '5학년 (초등)' },
    { value: '6', label: '6학년 (초등)' },
    { value: '7', label: '1학년 (중등)' },
    { value: '8', label: '2학년 (중등)' },
    { value: '9', label: '3학년 (중등)' },
    { value: '10', label: '1학년 (고등)' },
    { value: '11', label: '2학년 (고등)' },
    { value: '12', label: '3학년 (고등)' }
  ];

  const columnColors = [
    'from-blue-400 to-blue-500',
    'from-green-400 to-green-500',
    'from-purple-400 to-purple-500',
    'from-yellow-400 to-yellow-500',
    'from-pink-400 to-pink-500',
    'from-indigo-400 to-indigo-500',
    'from-red-400 to-red-500',
    'from-cyan-400 to-cyan-500',
  ];

  const phases = [
    { value: '11', label: '1학기 중간' },
    { value: '12', label: '1학기 기말' },
    { value: '13', label: '1학기 기타' },
    { value: '21', label: '2학기 중간' },
    { value: '22', label: '2학기 기말' },
    { value: '23', label: '2학기 기타' }
  ];

  // LaTeX 렌더링 캐시 (고성능 LRU + 우선순위)
  const latexCache = useRef(new Map());
  const latexAccessCount = useRef(new Map()); // 접근 빈도 추적
  const MAX_CACHE_SIZE = useRef(2000); // 동적 조정 가능
  const latexRenderQueue = useRef(new Set());
  const batchRenderQueue = useRef(new Set());
  const batchTimeoutRef = useRef(null);

  // Math Concepts 로드
  useEffect(() => {
    const loadMathConcepts = async () => {
      setIsLoadingMathConcepts(true);
      try {
        const concepts = await fetchMathConceptsUtil();
        setMathConcepts(concepts);
      } catch (error) {
        console.error('Error loading math concepts:', error);
      } finally {
        setIsLoadingMathConcepts(false);
      }
    };
    
    loadMathConcepts();
  }, []);

  // Helper 함수들
  const getConceptOrder = (conceptId) => getConceptOrderUtil(mathConcepts, conceptId);
  const getConceptById = (conceptId) => getConceptByIdUtil(mathConcepts, conceptId);

  // 동적 캐시 크기 조정
  const adjustCacheSize = useCallback(() => {
    const conceptCount = mathConcepts.length;
    const columnCount = columns.length;
    const estimatedItems = conceptCount * (1 + columnCount * 0.1); // 추정 필요 캐시 수
    MAX_CACHE_SIZE.current = Math.max(1000, Math.min(5000, estimatedItems * 2));
  }, [mathConcepts.length, columns.length]);

  // 스마트 캐시 정리 (접근 빈도 + LRU)
  const manageCacheSize = useCallback(() => {
    if (latexCache.current.size > MAX_CACHE_SIZE.current) {
      const entries = Array.from(latexCache.current.entries());
      
      // 접근 빈도와 최근성을 고려한 점수 계산
      const scoredEntries = entries.map(([key, value], index) => {
        const accessCount = latexAccessCount.current.get(key) || 1;
        const recency = entries.length - index; // 최근 사용된 것일수록 높은 점수
        const score = accessCount * 0.7 + recency * 0.3;
        return { key, value, score };
      });
      
      // 점수가 낮은 것부터 삭제 (25% 삭제)
      scoredEntries.sort((a, b) => a.score - b.score);
      const deleteCount = Math.floor(MAX_CACHE_SIZE.current * 0.25);
      
      for (let i = 0; i < deleteCount; i++) {
        latexCache.current.delete(scoredEntries[i].key);
        latexAccessCount.current.delete(scoredEntries[i].key);
      }
    }
  }, []);

  // 배치 렌더링 처리 함수 (더 이상 사용하지 않음 - 즉시 렌더링으로 변경됨)
  // const processBatchRender = useCallback(() => { ... }, [manageCacheSize]);

  // LaTeX 렌더링 (고성능 최적화)
  const renderLatex = useCallback((text) => {
    if (!text) return '';
    
    // 캐시 히트
    if (latexCache.current.has(text)) {
      const cached = latexCache.current.get(text);
      // 접근 빈도 증가
      const currentCount = latexAccessCount.current.get(text) || 0;
      latexAccessCount.current.set(text, currentCount + 1);
      
      // LRU 업데이트
      latexCache.current.delete(text);
      latexCache.current.set(text, cached);
      return cached;
    }
    
    // 중복 렌더링 방지
    if (latexRenderQueue.current.has(text)) {
      return text;
    }
    
    latexRenderQueue.current.add(text);
    
    // 단순 텍스트는 즉시 처리
    if (!text.includes('$')) {
      latexCache.current.set(text, text);
      latexAccessCount.current.set(text, 1);
      latexRenderQueue.current.delete(text);
      return text;
    }
    
    // LaTeX 포함 텍스트는 즉시 렌더링 (매스컨셉 상세 모달에서 정상 표시를 위해)
    try {
      const rendered = text.replace(/\$([^$]+)\$/g, (match, formula) => {
        try {
          return katex.renderToString(formula, {
            throwOnError: false,
            displayMode: false,
            strict: false,
            macros: {},
            trust: false // 보안 강화
          });
        } catch (e) {
          return match; // 렌더링 실패 시 원본 반환
        }
      });
      
      // 캐시에 저장
      latexCache.current.set(text, rendered);
      latexAccessCount.current.set(text, 1);
      latexRenderQueue.current.delete(text);
      
      return rendered; // 즉시 렌더링된 결과 반환
    } catch (error) {
      // 전체 렌더링 실패 시 원본 반환 및 캐시 저장
      latexCache.current.set(text, text);
      latexAccessCount.current.set(text, 1);
      latexRenderQueue.current.delete(text);
      return text;
    }
  }, []);

  // 캐시 크기 동적 조정
  useEffect(() => {
    adjustCacheSize();
  }, [adjustCacheSize]);

  // Context의 수학 개념 데이터 사용
  useEffect(() => {
    // 수학 개념이 없으면 로드 (Context에서 이미 관리하므로 추가 호출 불필요)
    setIsLoadingConcepts(isLoadingMathConcepts);
  }, [isLoadingMathConcepts]);

  // 분석 상태 표시
  const getAnalysisStatusDisplay = (analysis) => {
    switch (analysis) {
      case 'empty':
        return <span className="text-gray-600">미분석</span>;
      case 'waiting':
        return <span className="text-blue-600">분석 중</span>;
      case 'completed':
        return <span className="text-green-600">완료</span>;
      case 'outdated':
        return <span className="text-orange-600">업데이트 필요</span>;
      case 'error':
        return <span className="text-red-600">오류</span>;
      default:
        return <span className="text-gray-600">미분석</span>;
    }
  };

  // 칼럼명 자동 생성 함수
  const generateColumnName = (filters) => {
    const parts = [];
    
    // 학교명 처리
    if (filters.school_name.length > 0) {
      if (filters.school_name.length === 1) {
        // 단일 학교: 축약명 사용
        const school = schoolList.find(s => s.school_name === filters.school_name[0]);
        if (school) {
          parts.push(SchoolNameUtils.getAbbreviation(school.school_name, school.school_level));
        } else {
          parts.push(filters.school_name[0]);
        }
      } else if (filters.school_name.length <= 3) {
        // 2-3개 학교: 축약명을 쉼표로 연결
        const shortNames = filters.school_name.map(name => {
          const school = schoolList.find(s => s.school_name === name);
          if (school) {
            return SchoolNameUtils.getAbbreviation(school.school_name, school.school_level);
          }
          return name;
        });
        parts.push(shortNames.join(','));
      } else {
        parts.push(`${filters.school_name.length}개학교`);
      }
    }
    
    // 출제년도 처리
    if (filters.att_year.length > 0) {
      if (filters.att_year.length === 1) {
        parts.push(`${filters.att_year[0]}년`);
      } else if (filters.att_year.length <= 3) {
        const sortedYears = [...filters.att_year].sort();
        parts.push(`${sortedYears.join(',')}년`);
      } else {
        const sortedYears = [...filters.att_year].sort();
        parts.push(`${sortedYears[0]}-${sortedYears[sortedYears.length - 1]}년`);
      }
    }
    
    // 학년 처리
    if (filters.school_grade.length > 0) {
      if (filters.school_grade.length === 1) {
        const grade = grades.find(g => g.value === filters.school_grade[0]);
        parts.push(grade ? grade.label : `${filters.school_grade[0]}학년`);
      } else if (filters.school_grade.length <= 3) {
        const gradeLabels = filters.school_grade.map(gradeValue => {
          const grade = grades.find(g => g.value === gradeValue);
          return grade ? grade.label : `${gradeValue}학년`;
        });
        parts.push(gradeLabels.join(','));
      } else {
        parts.push(`${filters.school_grade.length}개학년`);
      }
    }
    
    // 시험구분 처리
    if (filters.phase.length > 0) {
      if (filters.phase.length === 1) {
        const phase = phases.find(p => p.value === filters.phase[0]);
        parts.push(phase ? phase.label : `시험구분${filters.phase[0]}`);
      } else if (filters.phase.length <= 3) {
        const phaseLabels = filters.phase.map(phaseValue => {
          const phase = phases.find(p => p.value === phaseValue);
          return phase ? phase.label : `시험구분${phaseValue}`;
        });
        parts.push(phaseLabels.join(','));
      } else {
        parts.push(`${filters.phase.length}개시험`);
      }
    }
    
    // 과목 처리
    if (filters.subject) {
      parts.push(filters.subject);
    }
    
    return parts.join('-') || '새 컬럼';
  };

  // 서버 연결 테스트 함수 (주석처리)
  // const testServerConnection = async () => {
  //   try {
  //     console.log('서버 연결 테스트 시작...');
  //     console.log('API Base URL:', process.env.REACT_APP_API_BASE_URL);
      
  //     const token = await getAccessToken();
  //     if (!token) {
  //       console.error('토큰을 가져올 수 없습니다.');
  //       return false;
  //     }

  //     const response = await fetch(
  //       `${process.env.REACT_APP_API_BASE_URL}/api/schoolexams/mathconcepts`,
  //       {
  //         method: 'GET',
  //         headers: {
  //           'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
  //           'Content-Type': 'application/json',
  //         }
  //       }
  //     );

  //     console.log('서버 연결 테스트 결과:', {
  //       status: response.status,
  //       statusText: response.statusText,
  //       ok: response.ok
  //     });

  //     return response.ok;
  //   } catch (error) {
  //     console.error('서버 연결 테스트 실패:', error);
  //     return false;
  //   }
  // };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => {
      const currentValues = prev[filterType] || [];
      let newFilters;
      
      if (filterType === 'subject') {
        newFilters = { ...prev, [filterType]: value };
      } else {
        if (currentValues.includes(value)) {
          newFilters = { ...prev, [filterType]: currentValues.filter(v => v !== value) };
        } else {
          newFilters = { ...prev, [filterType]: [...currentValues, value] };
        }
      }
      
      // 필터 변경 시 칼럼명 자동 업데이트
      const generatedName = generateColumnName(newFilters);
      setNewColumnName(generatedName);
      
      return newFilters;
    });
  };

  // 시험 ID 검색
  const handleSearchIds = async () => {
    try {
      setIsLoadingIds(true);
      const token = await getAccessToken();
      
      const hasFilters = Object.values(filters).some(value => 
        Array.isArray(value) ? value.length > 0 : value !== ''
      );
      
      if (!hasFilters) {
        alert('최소 하나의 필터 조건을 선택해주세요.');
        return;
      }

      const params = new URLSearchParams();
      
      if (filters.school_name.length > 0) {
        filters.school_name.forEach(name => {
          // school_list에서 해당 학교 찾기
          const school = schoolList.find(s => s.school_name === name);
          if (school) {
            // 축약명으로 변환
            const shortName = SchoolNameUtils.getAbbreviation(school.school_name, school.school_level);
            params.append('school_name', shortName);
          } else {
            // school_list에 없는 경우 (문제집 등) 원본 그대로 사용
            params.append('school_name', name);
          }
        });
      }
      if (filters.att_year.length > 0) {
        filters.att_year.forEach(year => params.append('att_year', year));
      }
      if (filters.school_grade.length > 0) {
        filters.school_grade.forEach(grade => params.append('school_grade', grade));
      }
      if (filters.phase.length > 0) {
        filters.phase.forEach(phase => params.append('phase', phase));
      }
      if (filters.subject) {
        params.append('subject', filters.subject);
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/schoolexams/list-ids?${params}`,
        {
          headers: {
            'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error('시험 검색에 실패했습니다.');
      }

      const result = await response.json();
      const ids = result.data.map(exam => exam.sxm_id);
      setAvailableIds(ids);
      
      // examDetails를 누적해서 저장 (기존 데이터 + 새로운 데이터)
      setExamDetails(prev => {
        const newExams = result.data || [];
        const existingIds = new Set(prev.map(exam => exam.sxm_id));
        const uniqueNewExams = newExams.filter(exam => !existingIds.has(exam.sxm_id));
        return [...prev, ...uniqueNewExams];
      });
      
      setSelectedIds([]);
    } catch (error) {
      // console.error('Error searching exams:', error);
      alert(error.message);
    } finally {
      setIsLoadingIds(false);
    }
  };



  // ID 선택/해제
  const handleIdToggle = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  // 전체 선택/해제
  const handleSelectAllIds = () => {
    if (selectedIds.length === availableIds.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds([...availableIds]);
    }
  };

  // 시험 분석 프리셋 등록
  const handleAddColumn = async () => {
    if (!newColumnName.trim()) {
      alert('프리셋 이름을 입력해주세요.');
      return;
    }

    if (selectedIds.length === 0) {
      alert('시험을 선택해주세요.');
      return;
    }

    if (selectedIds.length > 200) {
      alert('최대 200개의 시험만 선택할 수 있습니다.');
      return;
    }

    try {
      setIsLoading(true);

      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      const requestData = {
        sxm_ids: selectedIds,
        sxmset_name: newColumnName.trim()
      };
      
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/schoolexams/set`,
        {
          method: 'POST',
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData)
        }
      );

      if (response.status === 401) {
        navigate('/signin');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '프리셋 등록에 실패했습니다.');
      }

      const result = await response.json();
      alert('시험 분석 프리셋이 성공적으로 등록되었습니다.');
      
      // 모달 닫기 및 상태 초기화
      setShowAddColumnModal(false);
      setNewColumnName('');
      setSelectedIds([]);
      setAvailableIds([]);
      setFilters({
        school_name: [],
        att_year: [],
        school_grade: [],
        phase: [],
        subject: ''
      });
      setNewColumnSchoolSearch('');
      
    } catch (error) {
      console.error('Error creating preset:', error);
      alert(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // 기존 분석 데이터 처리 로직은 제거됨
  // 새로운 프리셋 API를 사용합니다

  // 컬럼 추가 함수 (기존 분석 결과 수집 로직)
  const handleAddColumn_OLD = async () => {
    if (!newColumnName.trim()) {
      alert('데이터명을 입력해주세요.');
      return;
    }

    if (selectedIds.length === 0) {
      alert('시험을 선택해주세요.');
      return;
    }

    setIsLoading(true);

    // 문제 개수 저장용
    let problemCounts = {};

    try {
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      // 새로운 통합 분석 API 호출
      const sxmIdsParam = selectedIds.join(',');
      let analysisResults = {};
      let examAnalysisData = {}; // 시험별 개별 데이터 저장
      
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/schoolexams/analysis?sxm_ids=${sxmIdsParam}`,
          {
            headers: {
              'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
              'Content-Type': 'application/json',
            }
          }
        );

        if (response.status === 401) {
          alert('인증이 만료되었습니다. 다시 로그인해주세요.');
          navigate('/signin');
          return;
        }

        if (!response.ok) {
          throw new Error(`API 요청 실패: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        console.log('API 응답 데이터:', result); // 디버깅용 로그
        
        // 문제 개수 계산 및 시험별 개별 데이터 저장
        if (result.data.sxm_list && Array.isArray(result.data.sxm_list)) {
          result.data.sxm_list.forEach(exam => {
            if (exam.sxm_id && exam.analysis_result) {
              let problemCount = 0;
              
              try {
                let analysisResult = exam.analysis_result;
                
                // 문자열인 경우 JSON 파싱
                if (typeof analysisResult === 'string') {
                  analysisResult = JSON.parse(analysisResult);
                }
                
                // 배열의 길이가 문제 개수
                if (Array.isArray(analysisResult)) {
                  problemCount = analysisResult.length;
                  
                  // 시험별 개별 데이터 저장 (각 문제의 개념 카운트)
                  examAnalysisData[exam.sxm_id] = {};
                  analysisResult.forEach(problem => {
                    if (problem && problem.matched_concept_ids) {
                      problem.matched_concept_ids.forEach(conceptId => {
                        if (!examAnalysisData[exam.sxm_id][conceptId]) {
                          examAnalysisData[exam.sxm_id][conceptId] = 0;
                        }
                        examAnalysisData[exam.sxm_id][conceptId]++;
                      });
                    }
                  });
                }
              } catch (e) {
                console.warn(`문제 개수 파싱 실패 for sxm_id ${exam.sxm_id}:`, e);
              }
              
              problemCounts[exam.sxm_id] = problemCount;
              console.log(`sxm_id ${exam.sxm_id}: ${problemCount}문제`);
            }
          });
        }
        
        // API 응답에서 분석 결과 추출
        if (result && result.data) {
          // Case 1: 새로운 API 응답 구조 처리 { sxm_list, summary, statistics }
          if (result.data.summary && Array.isArray(result.data.summary)) {
            console.log('summary 배열 데이터 처리:', result.data.summary.length, '개 항목');
            console.log('summary 샘플 데이터:', result.data.summary.slice(0, 3));
            
            result.data.summary.forEach((item, index) => {
              console.log(`summary[${index}]:`, item);
              
              // 다양한 구조 시도
              let conceptId = null;
              let count = 0;
              
              if (typeof item === 'object' && item !== null) {
                // { conceptId: 1, count: 5 } 형태
                if (item.conceptId !== undefined && item.count !== undefined) {
                  conceptId = parseInt(item.conceptId);
                  count = parseInt(item.count);
                }
                // { concept_id: 1, count: 5 } 형태
                else if (item.concept_id !== undefined && item.count !== undefined) {
                  conceptId = parseInt(item.concept_id);
                  count = parseInt(item.count);
                }
                // { id: 1, total: 5 } 형태
                else if (item.id !== undefined && item.total !== undefined) {
                  conceptId = parseInt(item.id);
                  count = parseInt(item.total);
                }
                // { id: 1, value: 5 } 형태
                else if (item.id !== undefined && item.value !== undefined) {
                  conceptId = parseInt(item.id);
                  count = parseInt(item.value);
                }
                // [conceptId, count] 배열 형태
                else if (Array.isArray(item) && item.length >= 2) {
                  conceptId = parseInt(item[0]);
                  count = parseInt(item[1]);
                }
                // 첫 번째와 두 번째 속성 사용
                else {
                  const keys = Object.keys(item);
                  if (keys.length >= 2) {
                    conceptId = parseInt(item[keys[0]]);
                    count = parseInt(item[keys[1]]);
                  }
                }
                
                // 유효한 데이터인 경우 저장
                if (!isNaN(conceptId) && !isNaN(count) && count > 0) {
                  analysisResults[conceptId] = count;
                  console.log(`개념 ${conceptId}: ${count}회 저장`);
                }
              }
            });
          }
          // Case 2: 직접 개념별 카운트 형태 { "1": 5, "2": 3, ... }
          else if (typeof result.data === 'object' && !Array.isArray(result.data)) {
            console.log('집계된 형태 데이터 처리:', result.data);
            // 숫자 키를 가진 객체인지 확인
            const keys = Object.keys(result.data);
            if (keys.length > 0 && keys.every(key => !isNaN(parseInt(key)))) {
              // 개념 ID를 숫자로 변환하여 저장
              keys.forEach(key => {
                const conceptId = parseInt(key);
                const count = parseInt(result.data[key]) || 0;
                if (count > 0) {
                  analysisResults[conceptId] = count;
                }
              });
            } else {
              console.log('예상하지 못한 객체 구조:', result.data);
            }
          }
          // Case 3: 시험별 분석 결과 배열 형태
          else if (Array.isArray(result.data)) {
            console.log('배열 형태 데이터 처리:', result.data.length, '개 항목');
            result.data.forEach((examData, index) => {
              console.log(`시험 ${index + 1} 데이터:`, examData);
              
              // 분석 결과 추출
              let analysisResult = null;
              
              // 다양한 필드명 시도
              if (examData.analysis_result) {
                analysisResult = examData.analysis_result;
              } else if (examData.analysis) {
                analysisResult = examData.analysis;
              } else if (examData.matched_concept_ids) {
                analysisResult = examData.matched_concept_ids;
              }
              
              if (analysisResult) {
                try {
                  // 문자열인 경우 JSON 파싱 시도
                  if (typeof analysisResult === 'string') {
                    analysisResult = JSON.parse(analysisResult);
                  }
                  
                  // 배열 형태의 분석 결과 처리
                  if (Array.isArray(analysisResult)) {
                    analysisResult.forEach(problem => {
                      if (problem && Array.isArray(problem.matched_concept_ids)) {
                        problem.matched_concept_ids.forEach(conceptId => {
                          const id = parseInt(conceptId);
                          if (!isNaN(id)) {
                            if (!analysisResults[id]) {
                              analysisResults[id] = 0;
                            }
                            analysisResults[id]++;
                          }
                        });
                      }
                    });
                  }
                  // 직접 개념 ID 배열인 경우
                  else if (Array.isArray(analysisResult)) {
                    analysisResult.forEach(conceptId => {
                      const id = parseInt(conceptId);
                      if (!isNaN(id)) {
                        if (!analysisResults[id]) {
                          analysisResults[id] = 0;
                        }
                        analysisResults[id]++;
                      }
                    });
                  }
                } catch (parseError) {
                  console.error('분석 결과 파싱 오류:', parseError, analysisResult);
                }
              }
            });
          }
          // Case 3: 다른 구조인 경우
          else {
            console.log('알 수 없는 데이터 구조:', typeof result.data, result.data);
          }
        } else {
          console.log('result.data가 없음:', result);
        }
        
        console.log('최종 분석 결과:', analysisResults);

        if (Object.keys(analysisResults).length === 0) {
          console.log('분석 결과가 비어있음:', {
            selectedIds,
            resultData: result.data,
            resultKeys: result.data ? Object.keys(result.data) : 'no data'
          });
          alert(`선택한 시험들에서 분석 결과를 찾을 수 없습니다.\n\n상세 정보:\n- 총 ${selectedIds.length}개 시험 요청\n- API 응답: ${result.data ? '데이터 있음' : '데이터 없음'}\n\n분석이 완료된 시험을 선택해주세요.\n\n콘솔을 확인하여 자세한 정보를 확인하세요.`);
          return;
        }
      } catch (apiError) {
        console.error('통합 분석 API 오류:', apiError);
        alert(`분석 데이터 조회 중 오류가 발생했습니다.\n\n오류: ${apiError.message}\n\n잠시 후 다시 시도해주세요.`);
        return;
      }

      // 성공적으로 데이터를 수집한 경우 컬럼 추가
      // 선택된 시험들의 상세 정보 가져오기
      const selectedExamDetails = examDetails.filter(exam => selectedIds.includes(exam.sxm_id));
      
      const newColumn = {
        id: Date.now().toString(),
        name: newColumnName.trim(),
        sxmIds: selectedIds,
        problemCounts: problemCounts, // 각 sxm_id별 문제 개수 저장
        examAnalysisData: examAnalysisData, // 시험별 개별 데이터 저장
        examDetails: selectedExamDetails // 시험 상세 정보 저장
      };

      setColumns(prev => {
        const newColumns = [...prev, newColumn];
        // 첫 번째 컬럼이 추가되면 차트 뷰로 전환
        if (prev.length === 0) {
          setViewMode('chart');
        }
        return newColumns;
      });
      setAnalysisData(prev => {
        const newMap = new Map(prev);
        newMap.set(newColumn.id, analysisResults);
        console.log('컬럼 데이터 저장:', {
          columnId: newColumn.id,
          columnName: newColumn.name,
          dataKeys: Object.keys(analysisResults),
          sampleData: Object.entries(analysisResults).slice(0, 5)
        });
        return newMap;
      });

      setShowAddColumnModal(false);
      setNewColumnName('');
      setSelectedIds([]);
      setAvailableIds([]);
      setFilters({
        school_name: [],
        att_year: [],
        school_grade: [],
        phase: [],
        subject: ''
      });
      setNewColumnSchoolSearch('');

      const message = `컬럼이 성공적으로 추가되었습니다! \n 📊 총 ${selectedIds.length}개 시험 \n ${Object.keys(analysisResults).length}개 수학 개념 데이터 수집`;

      // 알림 표시
      setNotification({ show: true, message, type: 'success' });
      setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 1000);
    } catch (error) {
      console.error('컬럼 추가 오류:', error);
      
      let userMessage = '컬럼 추가 중 오류가 발생했습니다.';
      
      if (error.message === 'Failed to fetch') {
        userMessage = `🚨 네트워크 연결 오류!\n\n📍 문제: 서버와의 연결에 실패했습니다.\n- 인터넷 연결을 확인해주세요\n- 서버 상태를 확인해주세요\n\n잠시 후 다시 시도해보세요.`;
      } else if (error.message) {
        userMessage = error.message;
      }
      
      // 에러 알림 표시
      setNotification({ show: true, message: userMessage, type: 'error' });
      setTimeout(() => setNotification({ show: false, message: '', type: 'error' }), 1000);
    } finally {
      setIsLoading(false);
    }
  };

  // 프리셋 리스트 조회
  const fetchPresetList = async (params = presetSearchParams) => {
    try {
      setIsLoadingPresets(true);
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.per_page) queryParams.append('per_page', params.per_page);
      if (params.author) queryParams.append('author', params.author);
      if (params.sxmset_name) queryParams.append('sxmset_name', params.sxmset_name);

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/schoolexams/set/list?${queryParams}`,
        {
          method: 'GET',
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.status === 401) {
        navigate('/signin');
        return;
      }

      if (response.status === 204) {
        setPresetList([]);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '프리셋 리스트 조회에 실패했습니다.');
      }

      const result = await response.json();
      setPresetList(result.data || []);
      setPresetTotalPages(result.total_pages || 1);
      setPresetTotalItems(result.total_items || 0);
    } catch (error) {
      console.error('Error fetching preset list:', error);
      alert(error.message);
    } finally {
      setIsLoadingPresets(false);
    }
  };

  // 탭이 리스트로 변경되면 데이터 로드
  useEffect(() => {
    if (modalTab === 'list' && showAddColumnModal) {
      fetchPresetList();
    }
  }, [modalTab, showAddColumnModal]);

  // 프리셋 검색
  const handlePresetSearch = () => {
    const newParams = { ...presetSearchParams, page: 1 };
    setPresetSearchParams(newParams);
    fetchPresetList(newParams);
  };

  // 프리셋 페이지 변경
  const handlePresetPageChange = (newPage) => {
    const newParams = { ...presetSearchParams, page: newPage };
    setPresetSearchParams(newParams);
    fetchPresetList(newParams);
  };

  // 프리셋 수정
  const handlePresetUpdate = async (presetId) => {
    if (!editingPresetName.trim()) {
      alert('프리셋 이름을 입력해주세요.');
      return;
    }

    try {
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      const requestData = {
        sxmset_id: presetId,
        sxmset_name: editingPresetName.trim()
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/schoolexams/set`,
        {
          method: 'PUT',
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData)
        }
      );

      if (response.status === 401) {
        navigate('/signin');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '프리셋 수정에 실패했습니다.');
      }

      alert('프리셋이 수정되었습니다.');
      setEditingPresetId(null);
      setEditingPresetName('');
      fetchPresetList(); // 리스트 새로고침
    } catch (error) {
      console.error('Error updating preset:', error);
      alert(error.message);
    }
  };

  // 프리셋 삭제
  const handlePresetDelete = async (presetId, presetName) => {
    if (!window.confirm(`"${presetName}" 프리셋을 삭제하시겠습니까?\n삭제된 프리셋은 복구할 수 없습니다.`)) {
      return;
    }

    try {
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      const requestData = {
        sxmset_id: presetId
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/schoolexams/set`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData)
        }
      );

      if (response.status === 401) {
        navigate('/signin');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '프리셋 삭제에 실패했습니다.');
      }

      alert('프리셋이 삭제되었습니다.');
      fetchPresetList(); // 리스트 새로고침
    } catch (error) {
      console.error('Error deleting preset:', error);
      alert(error.message);
    }
  };

  // 프리셋 편집 시작
  const handlePresetEditStart = (preset) => {
    setEditingPresetId(preset.sxmset_id);
    setEditingPresetName(preset.sxmset_name);
  };

  // 프리셋 편집 취소
  const handlePresetEditCancel = () => {
    setEditingPresetId(null);
    setEditingPresetName('');
  };

  // 분석 데이터 처리 함수
  const handleAnalysisDataProcess = (result, preset, presetDetail = null) => {
    // sxmList에서 sxm_id 배열 추출
    const sxmIds = presetDetail?.sxm_list?.map(exam => exam.sxm_id) || [];
    
    // 각 시험의 문제 개수 계산
    const problemCounts = {};
    if (presetDetail?.sxm_list) {
      presetDetail.sxm_list.forEach(exam => {
        problemCounts[exam.sxm_id] = exam.analysis_info?.problems || 0;
      });
    }
    
    // 분석 데이터를 컬럼으로 추가
    const newColumn = {
      id: Date.now(),
      name: preset.sxmset_name,
      presetId: preset.sxmset_id,
      sxmIds: sxmIds, // 기존 컬럼과 동일한 형식으로 sxmIds 추가
      problemCounts: problemCounts, // 문제 개수 정보 추가
      data: result.data.by_cid || {},
      metadata: {
        sheet_count: result.data.sheet_count,
        total_cid_count: result.data.total_cid_count,
        total_prob_count: result.data.total_prob_count,
        mean_P: result.data.mean_P,
        H_norm: result.data.H_norm,
        HHI_norm: result.data.HHI_norm,
        K: result.data.K,
        p_sum: result.data.p_sum,
        N_eff: result.data.N_eff,
        mu_sheet_cid: result.data.mu_sheet_cid,
        sigma_sheet_cid: result.data.sigma_sheet_cid,
        cv_sheet_cid: result.data.cv_sheet_cid,
        mu_sheet_prob: result.data.mu_sheet_prob,
        sigma_sheet_prob: result.data.sigma_sheet_prob,
        cv_sheet_prob: result.data.cv_sheet_prob,
        beta: result.data.beta
      },
      presetDetail: presetDetail, // 프리셋 상세 정보 저장
      sxmList: presetDetail?.sxm_list || [],
      createdAt: new Date().toISOString()
    };

    setColumns(prev => {
      const newColumns = [...prev, newColumn];
      // 첫 번째 컬럼이 추가되면 차트 뷰로 전환
      if (prev.length === 0) {
        setViewMode('chart');
      }
      return newColumns;
    });

    // analysisData Map에 새로운 분석 데이터 추가
    setAnalysisData(prevAnalysisData => {
      const newMap = new Map(prevAnalysisData);
      
      // by_cid 데이터를 적절한 형식으로 변환
      const processedData = {};
      Object.entries(result.data.by_cid).forEach(([cid, values]) => {
        processedData[cid] = {
          cnt: values[0],      // 관측 횟수
          f: values[1],        // 출현율
          f_prime: values[2],  // 보정 출현율
          cov: values[3],      // 커버리지
          cov_prime: values[4], // 보정 커버리지 (신규)
          p_beta: values[5]    // 존재감 점수 (인덱스 변경: 4 → 5)
        };
      });
      
      newMap.set(newColumn.id, processedData);
      return newMap;
    });

    // examDetails에 프리셋의 시험 정보 추가
    if (presetDetail?.sxm_list) {
      setExamDetails(prevExamDetails => {
        const newExamDetails = [...prevExamDetails];
        
        presetDetail.sxm_list.forEach(exam => {
          // 이미 존재하는지 확인
          const existingIndex = newExamDetails.findIndex(e => e.sxm_id === exam.sxm_id);
          if (existingIndex === -1) {
            // 새로운 시험 정보 추가
            newExamDetails.push({
              sxm_id: exam.sxm_id,
              school_name: exam.school_name,
              att_year: exam.att_year,
              school_grade: exam.school_grade,
              phase: exam.phase,
              subject: exam.subject,
              edu_version: exam.edu_version,
              analysis_info: exam.analysis_info
            });
          }
        });
        
        return newExamDetails;
      });
    }
    
    // 알림 표시
    setNotification({ 
      show: true, 
      message: `"${preset.sxmset_name}" 프리셋 데이터가 추가되었습니다.`, 
      type: 'success' 
    });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
  };

  // 프리셋 선택 시 분석 데이터 로드
  const handlePresetSelect = async (preset) => {
    try {
      setLoadingPresetId(preset.sxmset_id);
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      // 먼저 프리셋 상세 정보를 가져와서 sxm_ids를 획득
      const presetDetailResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/schoolexams/set?sxmset_id=${preset.sxmset_id}`,
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (presetDetailResponse.status === 401) {
        navigate('/signin');
        return;
      }

      if (!presetDetailResponse.ok) {
        const errorData = await presetDetailResponse.json();
        throw new Error(errorData.message || '프리셋 상세 정보 조회에 실패했습니다.');
      }

      const presetDetail = await presetDetailResponse.json();
      const sxmList = presetDetail.data.sxm_list || [];
      
      if (sxmList.length === 0) {
        throw new Error('프리셋에 시험이 포함되어 있지 않습니다.');
      }

      // sxm_list에서 sxm_id들을 추출
      const sxmIds = sxmList.map(exam => exam.sxm_id);

      // sxm_ids로 분석 API 호출
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/schoolexams/analysis-v2?sxm_ids=${sxmIds.join(',')}`,
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.status === 401) {
        navigate('/signin');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '분석 데이터 조회에 실패했습니다.');
      }

      const result = await response.json();
      
      // 프리셋 상세 정보와 함께 분석 데이터 처리
      handleAnalysisDataProcess(result, preset, presetDetail.data);

    } catch (error) {
      console.error('Error loading preset data:', error);
      alert(error.message);
    } finally {
      setLoadingPresetId(null);
    }
  };

  // 모달 닫기
  const handleCloseModal = () => {
    setShowAddColumnModal(false);
    setNewColumnName('');
    setSelectedIds([]);
    setAvailableIds([]);
    setFilters({
      school_name: [],
      att_year: [],
      school_grade: [],
      phase: [],
      subject: ''
    });
    setNewColumnSchoolSearch(''); // 학교 검색어 초기화
    setModalTab('list'); // 탭 초기화
    setPresetSearchParams({
      page: 1,
      per_page: 20,
      author: '',
      sxmset_name: ''
    });
    setPresetList([]);
    setEditingPresetId(null); // 편집 상태 초기화
    setEditingPresetName('');
    setLoadingPresetId(null); // 로딩 상태 초기화
  };

  // 컬럼 정렬 기능
  const handleColumnSort = (columnId) => {
    setSortConfig(prev => {
      if (prev.columnId === columnId) {
        // 같은 컬럼을 다시 클릭한 경우 순서 변경
        if (prev.direction === 'original') {
          return { columnId, direction: 'desc' };
        } else if (prev.direction === 'desc') {
          return { columnId, direction: 'asc' };
        } else {
          return { columnId: null, direction: 'original' };
        }
      } else {
        // 다른 컬럼을 클릭한 경우 내림차순부터 시작
        return { columnId, direction: 'desc' };
      }
    });
  };

  // 컬럼 삭제
  const handleRemoveColumn = (columnId) => {
    if (window.confirm('이 컬럼을 삭제하시겠습니까?')) {
      setColumns(prev => prev.filter(col => col.id !== columnId));
      setAnalysisData(prev => {
        const newMap = new Map(prev);
        newMap.delete(columnId);
        return newMap;
      });
      // 삭제된 컬럼이 정렬 중이었다면 정렬 상태 초기화
      setSortConfig(prev => prev.columnId === columnId ? { columnId: null, direction: 'original' } : prev);
    }
  };

  // 캐시된 셀 값 계산
  const cellValueCache = useRef(new Map());
  const cellValueCacheVersion = useRef(0);

  // 분석 데이터 변경 시 캐시 무효화
  useEffect(() => {
    cellValueCacheVersion.current++;
    cellValueCache.current.clear();
  }, [analysisData]);

  // 원본 셀 값 가져오기 (고성능 캐싱 + Map 최적화)
  const getOriginalCellValue = useCallback((conceptId, columnId) => {
    const cacheKey = `${conceptId}-${columnId}-${cellValueCacheVersion.current}`;
    
    // 캐시된 값 확인
    if (cellValueCache.current.has(cacheKey)) {
      return cellValueCache.current.get(cacheKey);
    }

    let result = 0;
    
    try {
      const columnData = analysisData.get(columnId);
      if (!columnData) {
        cellValueCache.current.set(cacheKey, 0);
        return 0;
      }
      
      // 빠른 경로: 직접 접근
      let value = columnData[conceptId];
      
      // 대안 경로: 타입 변환 시도 (필요시에만)
      if (value === undefined || value === null) {
        const strConceptId = String(conceptId);
        const numConceptId = Number(conceptId);
        
        value = columnData[strConceptId] ?? columnData[numConceptId] ?? 0;
      }
      
      // 숫자 변환
      if (value !== undefined && value !== null) {
        const numValue = Number(value);
        result = isNaN(numValue) ? 0 : numValue;
      }
      
    } catch (error) {
      console.error('Error in getCellValue:', error, { conceptId, columnId });
      result = 0;
    }

    // 결과 캐싱 (캐시 크기 제한)
    if (cellValueCache.current.size > 10000) {
      // 캐시가 너무 크면 절반 삭제 (LRU 방식)
      const entries = Array.from(cellValueCache.current.entries());
      cellValueCache.current.clear();
      entries.slice(-5000).forEach(([key, value]) => {
        cellValueCache.current.set(key, value);
      });
    }
    
    cellValueCache.current.set(cacheKey, result);
    return result;
  }, [analysisData]);



  // 학교별 문제 개수 계산
  const schoolProblemCounts = useMemo(() => {
    const counts = new Map();
    
    console.log('학교별 문제 개수 계산 시작 - columns:', columns.length);
    
    columns.forEach((column, colIndex) => {
      console.log(`컬럼 ${colIndex} (${column.name}):`, {
        sxmIds: column.sxmIds,
        problemCounts: column.problemCounts
      });
      
      if (column.sxmIds && Array.isArray(column.sxmIds) && column.problemCounts) {
        // 각 sxm_id별로 실제 문제 개수 사용
        column.sxmIds.forEach(sxmId => {
          const examDetail = examDetails.find(exam => exam.sxm_id === sxmId);
          if (examDetail && examDetail.school_name) {
            const schoolName = examDetail.school_name;
            const problemCount = column.problemCounts[sxmId] || 0;
            
            console.log(`학교: ${schoolName}, sxmId: ${sxmId}, 문제수: ${problemCount}`);
            counts.set(schoolName, (counts.get(schoolName) || 0) + problemCount);
          }
        });
      } else {
        console.log('컬럼에 problemCounts가 없음:', column);
      }
    });
    
    console.log('학교별 문제 개수 최종 결과:', Array.from(counts.entries()));
    return counts;
  }, [columns, examDetails]);

  // 전체 문제 수 계산 (모든 컬럼의 모든 학교 문제 수 합계)
  const totalProblemCount = useMemo(() => {
    let total = 0;
    const countedSchools = new Set();
    
    columns.forEach(column => {
      if (column.sxmIds && Array.isArray(column.sxmIds) && column.problemCounts) {
        column.sxmIds.forEach(sxmId => {
          const examDetail = examDetails.find(exam => exam.sxm_id === sxmId);
          if (examDetail && examDetail.school_name) {
            const schoolKey = `${examDetail.school_name}-${sxmId}`;
            if (!countedSchools.has(schoolKey)) {
              const problemCount = column.problemCounts[sxmId] || 0;
              total += problemCount;
              countedSchools.add(schoolKey);
            }
          }
        });
      }
    });
    
    console.log('전체 문제 수:', total);
    return total;
  }, [columns, examDetails]);

  // 출현률 계산 함수 (school_grade와 phase 기준)
  const getRateData = useCallback((originalValue, columnId, conceptId) => {
    const column = columns.find(col => col.id === columnId);
    if (!column || !column.sxmIds) return 0;
    
    // 각 school_grade와 phase 조합별로 데이터 그룹화
    const groupedData = new Map(); // key: "grade_phase", value: { conceptCount, problemCount, sxmIds }
    
    column.sxmIds.forEach(sxmId => {
      const examDetail = examDetails.find(exam => exam.sxm_id === sxmId);
      if (!examDetail) return;
      
      const groupKey = `${examDetail.school_grade}_${examDetail.phase}`;
      
      if (!groupedData.has(groupKey)) {
        groupedData.set(groupKey, {
          conceptCount: 0,
          problemCount: 0,
          sxmIds: []
        });
      }
      
      const group = groupedData.get(groupKey);
      group.sxmIds.push(sxmId);
      group.problemCount += column.problemCounts?.[sxmId] || 0;
      
      // 시험별 개별 데이터에서 개념 출현 횟수 계산
      if (column.examAnalysisData && column.examAnalysisData[sxmId]) {
        const examData = column.examAnalysisData[sxmId];
        if (examData[conceptId]) {
          group.conceptCount += examData[conceptId];
        }
      }
    });
    
    // 각 그룹별로 출현율 계산 후 평균
    let totalRate = 0;
    let groupCount = 0;
    
    groupedData.forEach((group, groupKey) => {
      if (group.problemCount > 0 && group.conceptCount > 0) {
        const groupRate = (group.conceptCount / group.problemCount) * 100;
        totalRate += groupRate;
        groupCount++;
        console.log(`그룹 ${groupKey}: 개념출현 ${group.conceptCount}회 / 문제수 ${group.problemCount} = ${groupRate.toFixed(2)}%`);
      }
    });
    
    // 평균 출현율 반환
    if (groupCount === 0) return 0;
    const averageRate = totalRate / groupCount;
    
    console.log('최종 평균 출현율:', averageRate.toFixed(2));
    return parseFloat(averageRate.toFixed(2));
  }, [columns, examDetails]);

  // 데이터 스케일에 따른 셀 값 가져오기 (출현률 적용)
  const getCellValue = useCallback((conceptId, columnId) => {
    const originalValue = getOriginalCellValue(conceptId, columnId);
    
    // 새로운 분석 데이터 처리 (프리셋 데이터)
    if (['cnt', 'f', 'f_prime', 'cov', 'p_beta'].includes(dataScale)) {
      const columnData = analysisData.get(columnId);
      if (columnData && columnData[conceptId]) {
        const data = columnData[conceptId];
        switch(dataScale) {
          case 'cnt':
            return data.cnt || 0;
          case 'f':
            return data.f ? `${(data.f * 100).toFixed(2)}%` : '0%'; // 백분율로 변환 + % 표시
          case 'f_prime':
            return data.f_prime ? (data.f_prime * 100).toFixed(2) : 0; // 백분율로 변환
          case 'cov':
            return data.cov ? `${(data.cov * 100).toFixed(2)}%` : '0%'; // 백분율로 변환 + % 표시
          case 'p_beta':
            return data.p_beta ? (data.p_beta * 100).toFixed(2) : 0; // 백분율로 변환
          default:
            return 0;
        }
      }
      // 새로운 데이터 스케일에서 해당 컬럼의 데이터가 없으면 0 반환
      return dataScale === 'f' || dataScale === 'cov' ? '0%' : 0;
    }
    
    // 기존 출현률 모드 적용
    if (dataScale === 'rate') {
      return getRateData(originalValue, columnId, conceptId);
    }
    
    // 기본값: 원본 데이터 사용
    return originalValue;
  }, [getOriginalCellValue, dataScale, getRateData, analysisData]);

  // 빈 행 체크 최적화 (배치 계산)
  const emptyRowsCache = useRef(new Map());
  const emptyRowsCacheVersion = useRef(0);

  // 컬럼이나 데이터 변경 시 빈 행 캐시 무효화
  useEffect(() => {
    emptyRowsCacheVersion.current++;
    emptyRowsCache.current.clear();
  }, [columns, analysisData]);

  const isRowEmpty = useCallback((conceptId) => {
    if (!hideEmptyRows) return false;
    
    const cacheKey = `${conceptId}-${emptyRowsCacheVersion.current}`;
    if (emptyRowsCache.current.has(cacheKey)) {
      return emptyRowsCache.current.get(cacheKey);
    }
    
    const isEmpty = columns.every(column => {
      const value = getCellValue(conceptId, column.id);
      // 문자열인 경우 (% 포함) '0%'와 비교, 숫자인 경우 0과 비교
      if (typeof value === 'string') {
        return value === '0%' || value === '0';
      }
      return value === 0;
    });
    
    emptyRowsCache.current.set(cacheKey, isEmpty);
    return isEmpty;
  }, [hideEmptyRows, columns, getCellValue]);

  // 필터링 및 정렬된 개념 목록 (메모화) - order 정보 추가
  const filteredConcepts = useMemo(() => {
    let concepts = mathConcepts
      .map(concept => ({
        ...concept,
        order: getConceptOrder(concept.id) // order 정보 추가
      }))
      .filter(concept => !isRowEmpty(concept.id));
    
    // 테이블 뷰에서 정렬 적용 
    if (viewMode === 'table' && sortConfig.columnId && sortConfig.direction !== 'original') {
      concepts = [...concepts].sort((a, b) => {
        let aValue = getCellValue(a.id, sortConfig.columnId);
        let bValue = getCellValue(b.id, sortConfig.columnId);
        
        // 문자열인 경우 숫자로 변환
        if (typeof aValue === 'string') {
          aValue = parseFloat(aValue.replace('%', '')) || 0;
        }
        if (typeof bValue === 'string') {
          bValue = parseFloat(bValue.replace('%', '')) || 0;
        }
        
        if (sortConfig.direction === 'desc') {
          return bValue - aValue;
        } else if (sortConfig.direction === 'asc') {
          return aValue - bValue;
        }
        return 0;
      });
    }
    
    return concepts;
  }, [mathConcepts, isRowEmpty, viewMode, sortConfig, getCellValue, getConceptOrder]);

  // 전체 데이터의 최대값 계산 (그라데이션용)
  const maxValue = useMemo(() => {
    let max = 0;
    columns.forEach(column => {
      mathConcepts.forEach(concept => {
        const value = getCellValue(concept.id, column.id);
        // 문자열인 경우 숫자 부분만 추출
        let numValue = value;
        if (typeof value === 'string') {
          numValue = parseFloat(value.replace('%', '')) || 0;
        }
        if (numValue > max) {
          max = numValue;
        }
      });
    });
    return max;
  }, [columns, mathConcepts, getCellValue]);

  // 학교별 합산 데이터 계산
  const schoolSummaryData = useMemo(() => {
    if (columns.length === 0) return [];
    
    const schoolMap = new Map();
    
    columns.forEach(column => {
      if (column.sxmIds && Array.isArray(column.sxmIds)) {
        // 컬럼의 전체 합산 계산 (한 번만)
        let columnTotal = 0;
        mathConcepts.forEach(concept => {
          columnTotal += getOriginalCellValue(concept.id, column.id);
        });
        
        // 학교별 시험 개수 계산
        const schoolExamCount = new Map();
        column.sxmIds.forEach(sxmId => {
          const examDetail = examDetails.find(exam => exam.sxm_id === sxmId);
          if (examDetail && examDetail.school_name) {
            const schoolName = examDetail.school_name;
            schoolExamCount.set(schoolName, (schoolExamCount.get(schoolName) || 0) + 1);
          }
        });
        
        // 각 학교에 시험 개수 비율로 분배
        const totalExamsInColumn = column.sxmIds.length;
        schoolExamCount.forEach((examCount, schoolName) => {
          const schoolRatio = examCount / totalExamsInColumn;
          const schoolContribution = columnTotal * schoolRatio;
          
          schoolMap.set(schoolName, (schoolMap.get(schoolName) || 0) + schoolContribution);
        });
      }
    });
    
    // 학교별 합산 결과를 배열로 변환하고 합산값 기준으로 정렬
    const result = Array.from(schoolMap.entries())
      .map(([schoolName, total]) => { 
        const problemCount = schoolProblemCounts.get(schoolName) || 0;
        console.log(`학교별 합산 데이터: ${schoolName}, total: ${Math.round(total)}, problemCount: ${problemCount}`);
        return {
          schoolName, 
          total: Math.round(total),
          problemCount: problemCount
        };
      })
      .sort((a, b) => b.total - a.total);
    
    console.log('최종 schoolSummaryData:', result);
    return result;
  }, [columns, examDetails, mathConcepts, getOriginalCellValue, schoolProblemCounts]);



  // 차트 데이터 계산 (각 개념별 컬럼별 데이터)
  const chartData = useMemo(() => {
    let data = mathConcepts.map(concept => {
      const columnValues = columns.map(column => {
        const originalValue = getOriginalCellValue(concept.id, column.id);
        let scaledValue = originalValue;
        
        // 데이터 스케일 모드 적용
        if (dataScale === 'rate') {
          scaledValue = getRateData(originalValue, column.id, concept.id);
        } else if (['cnt', 'f', 'f_prime', 'cov', 'p_beta'].includes(dataScale)) {
          const columnData = analysisData.get(column.id);
          if (columnData && columnData[concept.id]) {
            const data = columnData[concept.id];
            switch(dataScale) {
              case 'cnt':
                scaledValue = data.cnt || 0;
                break;
              case 'f':
                scaledValue = data.f ? `${(data.f * 100).toFixed(2)}%` : '0%';
                break;
              case 'f_prime':
                scaledValue = data.f_prime ? parseFloat((data.f_prime * 100).toFixed(2)) : 0;
                break;
              case 'cov':
                scaledValue = data.cov ? `${(data.cov * 100).toFixed(2)}%` : '0%';
                break;
              case 'p_beta':
                scaledValue = data.p_beta ? parseFloat((data.p_beta * 100).toFixed(2)) : 0;
                break;
              default:
                scaledValue = 0;
            }
          } else {
            // 데이터가 없을 때도 데이터 타입에 맞게 처리
            scaledValue = (dataScale === 'f' || dataScale === 'cov') ? '0%' : 0;
          }
        }
        
        return {
          columnId: column.id,
          columnName: column.name,
          value: scaledValue,
          originalValue: originalValue // 원본 값도 보존
        };
      });
      
      const total = columnValues.reduce((sum, col) => {
        const numValue = typeof col.value === 'string' ? parseFloat(col.value.replace('%', '')) || 0 : col.value;
        return sum + numValue;
      }, 0);
      
      return {
        id: concept.id,
        text: concept.text,
        order: concept.order,
        total: total,
        columns: columnValues,
        originalIndex: concept.id // 원래 순서 유지를 위해
      };
    });

    // 빈 행 숨기기 옵션 적용
    if (hideEmptyRows) {
      data = data.filter(item => item.total > 0);
    }

    // 정렬 적용
    if (sortConfig.columnId && sortConfig.direction !== 'original') {
      data.sort((a, b) => {
        let aValue = a.columns.find(col => col.columnId === sortConfig.columnId)?.value || 0;
        let bValue = b.columns.find(col => col.columnId === sortConfig.columnId)?.value || 0;
        
        // 문자열인 경우 숫자로 변환
        if (typeof aValue === 'string') {
          aValue = parseFloat(aValue.replace('%', '')) || 0;
        }
        if (typeof bValue === 'string') {
          bValue = parseFloat(bValue.replace('%', '')) || 0;
        }
        
        if (sortConfig.direction === 'desc') {
          return bValue - aValue;
        } else if (sortConfig.direction === 'asc') {
          return aValue - bValue;
        }
        return 0;
      });
    } else {
      // 원래 순서 (개념 ID 순)
      data.sort((a, b) => a.originalIndex - b.originalIndex);
    }
    
    console.log('차트 데이터 계산:', {
      totalConcepts: mathConcepts.length,
      totalColumns: columns.length,
      filteredData: data.length,
      sortConfig: sortConfig,
      sampleData: data.slice(0, 2).map(item => ({
        id: item.id,
        total: item.total,
        columns: item.columns
      })),
      analysisDataSize: analysisData.size
    });
    
    return data;
  }, [mathConcepts, columns, getCellValue, analysisData, hideEmptyRows, sortConfig, dataScale, getRateData]);

  // 차트 데이터의 최대값 (각 컬럼별 개별 값들 중 최대값)
  const chartMaxValue = useMemo(() => {
    let maxValue = 1;
    chartData.forEach(item => {
      item.columns.forEach(col => {
        // 문자열인 경우 숫자 부분만 추출
        const numValue = typeof col.value === 'string' ? parseFloat(col.value.replace('%', '')) || 0 : col.value;
        if (numValue > maxValue) {
          maxValue = numValue;
        }
      });
    });
    return maxValue;
  }, [chartData]);

  // 단순한 스크롤 처리
  const tableContainerRef = useRef(null);

  // 단순한 스크롤 핸들러
  const handleScrollSimple = useCallback((e) => {
    handleScroll(e);
  }, [handleScroll]);

  // 컴포넌트 언마운트 시 캐시 정리
  useEffect(() => {
    return () => {
      // 배치 타이머 정리
      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
      }
      
      // 캐시 크기를 적당한 수준으로 줄임 (자주 사용되는 것만 유지)
      if (latexCache.current.size > 200) {
        const entries = Array.from(latexCache.current.entries());
        const scoredEntries = entries.map(([key, value]) => ({
          key, 
          value, 
          score: latexAccessCount.current.get(key) || 1
        }));
        
        // 접근 빈도가 높은 상위 200개만 유지
        scoredEntries.sort((a, b) => b.score - a.score);
        latexCache.current.clear();
        latexAccessCount.current.clear();
        
        scoredEntries.slice(0, 200).forEach(({ key, value, score }) => {
          latexCache.current.set(key, value);
          latexAccessCount.current.set(key, score);
        });
      }
      
      latexRenderQueue.current.clear();
      batchRenderQueue.current.clear();
    };
  }, []);

  if (isLoadingConcepts) {
    return (
      <div className="p-6">
        <div className="py-12 text-center">
          <div className="text-lg text-gray-600">수학 개념 데이터를 불러오는 중...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="py-12 text-center">
          <div className="mb-4 text-lg text-red-600">오류가 발생했습니다</div>
          <div className="mb-4 text-sm text-gray-600">{error}</div>
          <button
            onClick={() => {
              setError(null);
              navigate(location.pathname + location.search, { replace: true });
            }}
            className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
          >
            페이지 새로고침
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="flex flex-1 flex-col overflow-hidden p-4"
    >
      {/* 알림 메시지 */}
      {notification.show && (
        <div
          className={`fixed top-4 right-4 z-50 px-6 py-1 rounded-lg shadow-lg transition-all duration-500 ${
            notification.type === 'success' 
              ? 'bg-green-500/70 text-white backdrop-blur-sm' 
              : 'bg-red-500/70 text-white backdrop-blur-sm'
          }`}
          style={{ 
            animation: 'slideIn 0.3s ease-out',
            maxWidth: '400px'
          }}
        >
          <div className="flex items-center gap-2">
            {notification.type === 'success' ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            )}
            <span className="text-sm font-medium" style={{ whiteSpace: 'pre-line' }}>{notification.message}</span>
          </div>
        </div>
      )}
      
      <style jsx>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
      <div className="shrink-0">
        {/* 헤더 영역 - SchoolExamManagement 스타일 통일 */}
        <div className="shrink-0 bg-white">
          <div>
            <div className="mb-4 flex items-center gap-3">
              <h3 className="text-lg font-semibold">분석 결과 테이블</h3>
              <p className="text-xs text-gray-500">
                - {viewMode === 'table' ? (
                  <>
                    총 <span className="font-semibold text-primary">{mathConcepts.length}</span>개 수학 개념 중 <span className="font-semibold text-primary">{filteredConcepts.length}</span>개 표시
                  </>
                ) : (
                  <>
                    데이터가 있는 <span className="font-semibold text-primary">{chartData.length}</span>개 수학 개념 표시
                  </>
                )}
              </p>
            </div>
            
            {/* 뷰 모드 버튼, 빈 행 숨기기 버튼, 컬럼 정보 */}
            {columns.length > 0 && (
              <div className="flex justify-between items-center mb-1">
                <div className="flex items-center gap-2">
                  {/* 뷰 방식 선택 - 가장 좌측 */}
                  <div className="flex items-center rounded-3xl bg-gray-100 p-0.5">
                    <button
                      onClick={() => setViewMode('table')}
                      className={`flex items-center gap-1 rounded-3xl px-2 py-0.5 text-xs transition-colors ${
                        viewMode === 'table'
                          ? 'bg-white text-gray-700 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                      title="테이블 뷰"
                    >
                      <Table className="size-3" />
                      {/* 테이블 */}
                    </button>
                    <button
                      onClick={() => setViewMode('chart')}
                      className={`flex items-center gap-1 rounded-3xl px-2 py-0.5 text-xs transition-colors ${
                        viewMode === 'chart'
                          ? 'bg-white text-gray-700 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                      title="차트 뷰"
                    >
                      <BarChart3 className="size-3" />
                      {/* 차트 */}
                    </button>
                  </div>
                  
                  {/* 데이터 스케일 선택 - 중앙 */}
                  <div className="flex items-center gap-0.5 flex-wrap">
                    {/* <div className="flex items-center rounded-3xl bg-blue-50 p-0.5">
                      <button
                        onClick={() => setDataScale('normal')}
                        className={`flex items-center gap-1 rounded-3xl px-2 py-0.5 text-xs transition-colors ${
                          dataScale === 'normal'
                            ? 'bg-white text-blue-700 shadow-sm'
                            : 'text-blue-500 hover:text-blue-700'
                        }`}
                        title="원본 데이터"
                      >
                        원본
                      </button>
                      <button
                        onClick={() => setDataScale('rate')}
                        className={`flex items-center gap-1 rounded-3xl px-2 py-0.5 text-xs transition-colors ${
                          dataScale === 'rate'
                            ? 'bg-white text-blue-700 shadow-sm'
                            : 'text-blue-500 hover:text-blue-700'
                        }`}
                        title="학년·시험구분별 평균 출현률 (백분율)"
                      >
                        출현률
                      </button>
                    </div> */}
                    <div className="flex items-center rounded-3xl bg-green-50 p-0.5">
                      <button
                        onClick={() => setDataScale('cnt')}
                        className={`flex items-center gap-1 rounded-3xl px-2 py-0.5 text-xs transition-colors ${
                          dataScale === 'cnt'
                            ? 'bg-white text-green-700 shadow-sm'
                            : 'text-green-600 hover:text-green-700'
                        }`}
                        title="관측 횟수"
                      >
                        cnt
                      </button>
                      <button
                        onClick={() => setDataScale('f')}
                        className={`flex items-center gap-1 rounded-3xl px-2 py-0.5 text-xs transition-colors ${
                          dataScale === 'f'
                            ? 'bg-white text-green-700 shadow-sm'
                            : 'text-green-600 hover:text-green-700'
                        }`}
                        title="출현율 (백분율)"
                      >
                        f
                      </button>
                      <button
                        onClick={() => setDataScale('cov')}
                        className={`flex items-center gap-1 rounded-3xl px-2 py-0.5 text-xs transition-colors ${
                          dataScale === 'cov'
                            ? 'bg-white text-green-700 shadow-sm'
                            : 'text-green-600 hover:text-green-700'
                        }`}
                        title="커버리지 (백분율)"
                      >
                        cov
                      </button>
                      <button
                        onClick={() => setDataScale('p_beta')}
                        className={`flex items-center gap-1 rounded-3xl px-2 py-0.5 text-xs transition-colors ${
                          dataScale === 'p_beta'
                            ? 'bg-white text-green-700 shadow-sm'
                            : 'text-green-600 hover:text-green-700'
                        }`}
                        title="존재감 점수"
                      >
                        p_β
                      </button>
                    </div>
                  </div>
                  
                  {/* 학교별 합산 정보 버튼 */}
                  {schoolSummaryData.length > 0 && (
                    <button
                      onClick={() => setShowSchoolSummaryModal(true)}
                      className="flex items-center gap-1 rounded-3xl px-3 py-1 text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                      title="학교별 합산 정보 보기"
                    >
                      <Info className="size-3" />
                      {/* 정보 */}
                    </button>
                  )}
                </div>
                
                {/* 빈 행 숨기기 버튼 - 가장 우측 */}
                <button
                  onClick={() => setHideEmptyRows(!hideEmptyRows)}
                  className={`flex items-center gap-2 rounded-3xl px-3 py-1 text-xs ${
                    hideEmptyRows 
                      ? 'bg-blue-500 text-white hover:bg-blue-600' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {hideEmptyRows ? <Eye className="size-3" /> : <EyeOff className="size-3" />}
                  {/* {hideEmptyRows ? '모든 행 보기' : '빈 행 숨기기'} */}
                </button>
                
              </div>
            )}
          </div>
        </div>
      </div>

            {/* 컨텐츠 영역 */}
      {columns.length === 0 ? (
        <div className="flex flex-1 items-center justify-center rounded border border-gray-200 bg-gray-50">
          <div className="text-center">
            <div className="mb-4 text-gray-400">
              <Plus className="mx-auto size-16" strokeWidth={1} />
            </div>
            <h3 className="mb-2 text-lg font-medium text-gray-700">데이터를 추가하세요</h3>
            <p className="mb-6 text-sm text-gray-500">
              컬럼을 추가하여 분석 결과를 확인할 수 있습니다.
            </p>
          </div>
        </div>
      ) : viewMode === 'table' ? (
        filteredConcepts.length > 0 ? (
          <VirtualizedTable
            concepts={filteredConcepts}
            columns={columns}
            getCellValue={getCellValue}
            renderLatex={renderLatex}
            isMobileView={isMobileView}
            maxValue={maxValue}
            containerHeight={containerHeight}
            itemHeight={itemHeight}
            isLoadingConcepts={isLoadingConcepts}
            mathConceptsLength={mathConcepts.length}
            mathConcepts={mathConcepts}
            sortConfig={sortConfig}
            handleColumnSort={handleColumnSort}
            handleRemoveColumn={handleRemoveColumn}
            mathConceptWidth={mathConceptWidth}
            shouldTruncate={shouldTruncate}
            getConceptById={getConceptById}
            onOrderClick={fetchMathceptDetails}
            schoolSummaryData={schoolSummaryData}
            SchoolNameUtils={SchoolNameUtils}
            schoolList={schoolList}
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <div className="text-center text-gray-500">
              <p className="text-lg font-medium">표시할 데이터가 없습니다</p>
              <p className="mt-2 text-sm">
                {hideEmptyRows ? '모든 행이 비어있습니다. "빈 행 숨기기"를 해제해보세요.' : '컬럼을 추가하거나 데이터를 선택해주세요.'}
              </p>
            </div>
          </div>
        )
      ) : (
        <div className="min-h-0 flex-1 rounded border border-gray-200 bg-white p-2 overflow-hidden">
          <ChartView 
            chartData={chartData}
            chartMaxValue={chartMaxValue}
            renderLatex={renderLatex}
            isMobileView={isMobileView}
            columns={columns}
            sortConfig={sortConfig}
            handleColumnSort={handleColumnSort}
            containerHeight={containerHeight}
            mathConceptWidth={mathConceptWidth}
            shouldTruncate={shouldTruncate}
            getConceptById={getConceptById}
            onOrderClick={fetchMathceptDetails}
            schoolSummaryData={schoolSummaryData}
            SchoolNameUtils={SchoolNameUtils}
            schoolList={schoolList}
            setShowSchoolSummaryModal={setShowSchoolSummaryModal}
          />
        </div>
      )}

      {/* 플로팅 컬럼 추가 버튼 (모바일 & 데스크톱) */}
      {typeof document !== 'undefined' && (
        <FloatingButton
          variant="plus"
          onClick={() => {
            setShowAddColumnModal(true);
            setNewColumnName('새 프리셋');
          }}
          title="프리셋 추가"
          usePortal={true}
          // bottom={isMobileView ? `calc(5rem - 40px + 68px + env(safe-area-inset-bottom, 0px))` : '2rem'}
          leftOffsetPx={isMobileView ? 0 : (isExpanded ? 90 : 45)}
        />
      )}

      {/* 컬럼 추가 모달 */}
      {showAddColumnModal && typeof document !== 'undefined' && createPortal(
        <div 
          className="fixed inset-0 flex items-center justify-center"
          onClick={handleCloseModal}
          style={{
            zIndex: 9999980,
            paddingTop: isMobileView ? 'env(safe-area-inset-top, 0px)' : '0px',
            paddingBottom: isMobileView ? 'calc(68px + env(safe-area-inset-bottom, 0px))' : '0px',
            backgroundColor: 'rgba(0, 0, 0, 0.3)'
          }}
        >
          <div 
            className="flex w-[95vw] flex-col rounded-3xl bg-white lg:w-[800px]"
            onClick={(e) => e.stopPropagation()}
            style={{
              height: 'auto',
              maxHeight: isMobileView ? 'calc((100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)) * 0.75)' : '90vh'
            }}
          >
            <div className="flex items-center justify-between border-b p-6">
              <h3 className="text-lg font-semibold">시험 분석 프리셋 관리</h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="size-5" />
              </button>
            </div>
            
            {/* 탭 헤더 */}
            <div className="flex border-b">
              <button
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  modalTab === 'list'
                    ? 'border-b-2 border-primary text-primary bg-primary/5'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
                onClick={() => setModalTab('list')}
              >
                프리셋 리스트
              </button>
              <button
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  modalTab === 'add'
                    ? 'border-b-2 border-primary text-primary bg-primary/5'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
                onClick={() => setModalTab('add')}
              >
                프리셋 추가
              </button>
            </div>
            
            <div className="flex-1 space-y-6 overflow-y-auto p-6">
              {/* 프리셋 추가 탭 */}
              {modalTab === 'add' && (
                <>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      프리셋 이름 *
                    </label>
                    <input
                      type="text"
                      value={newColumnName}
                      onChange={(e) => setNewColumnName(e.target.value)}
                      className="w-full rounded border px-3 py-2"
                      placeholder="예: 청원고 테스트"
                    />
                  </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  필터 조건 (최소 1개 선택) *
                </label>
                
                <div className="grid grid-cols-2 items-end gap-4">
                  <div>
                    <label className="mb-1 flex items-center justify-between text-xs text-gray-600">
                      <div className="flex bg-gray-200 rounded-full p-[1px]">
                        <button
                          type="button"
                          onClick={() => setSearchInputType('school')}
                          className={`px-2.5 py-0.5 text-xs rounded-full transition-all duration-200 ${
                            searchInputType === 'school'
                              ? 'bg-white text-[#4ABC85] shadow-sm'
                              : 'text-gray-600 hover:text-gray-800'
                          }`}
                        >
                          학교
                        </button>
                        <button
                          type="button"
                          onClick={() => setSearchInputType('workbook')}
                          className={`px-2.5 py-0.5 text-xs rounded-full transition-all duration-200 ${
                            searchInputType === 'workbook'
                              ? 'bg-white text-[#4ABC85] shadow-sm'
                              : 'text-gray-600 hover:text-gray-800'
                          }`}
                        >
                          문제집
                        </button>
                      </div>
                      <span>({filters.school_name.length}/5)</span>
                    </label>
                    <div className="h-32 overflow-y-auto rounded border p-2">
                      <div className="mb-2">
                        <SchoolSearchInput
                          value={newColumnSchoolSearch}
                          onChange={setNewColumnSchoolSearch}
                          onSchoolSelect={(school) => {
                            const itemName = searchInputType === 'school' ? school.school_name : school;
                            if (filters.school_name.length < 5 && !filters.school_name.includes(itemName)) {
                              handleFilterChange('school_name', itemName);
                              setNewColumnSchoolSearch(''); // 선택 후 검색어 초기화
                            }
                          }}
                          placeholder={searchInputType === 'school' ? "학교를 검색하여 추가" : "문제집명을 입력하세요"}
                          isSearchOnly={true}
                          forceInputType={searchInputType}
                        />
                      </div>
                      {/* 선택된 학교 목록 */}
                      {filters.school_name.map(schoolName => (
                        <div key={schoolName} className="flex items-center justify-between py-1 text-sm">
                          <span>{schoolName}</span>
                          <button
                            onClick={() => handleFilterChange('school_name', schoolName)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="size-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs text-gray-600">
                      출제년도 ({filters.att_year.length}/5)
                    </label>
                    <div className="max-h-32 overflow-y-auto rounded border p-2">
                      {years.map(year => {
                        const isSelected = filters.att_year.includes(year);
                        const isDisabled = !isSelected && filters.att_year.length >= 5;
                        return (
                          <label key={year} className="flex items-center py-1 text-sm">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={isDisabled}
                              onChange={() => handleFilterChange('att_year', year)}
                              className="mr-2"
                            />
                            <span className={isDisabled ? 'text-gray-400' : ''}>
                              {year}년
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs text-gray-600">
                      학년 ({filters.school_grade.length}/5)
                    </label>
                    <div className="max-h-32 overflow-y-auto rounded border p-2">
                      {[...grades].reverse().map(grade => {
                        const isSelected = filters.school_grade.includes(grade.value);
                        const isDisabled = !isSelected && filters.school_grade.length >= 5;
                        return (
                          <label key={grade.value} className="flex items-center py-1 text-sm">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={isDisabled}
                              onChange={() => handleFilterChange('school_grade', grade.value)}
                              className="mr-2"
                            />
                            <span className={isDisabled ? 'text-gray-400' : ''}>
                              {grade.label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs text-gray-600">
                      시험구분 ({filters.phase.length}/5)
                    </label>
                    <div className="max-h-32 overflow-y-auto rounded border p-2">
                      {phases.map(phase => {
                        const isSelected = filters.phase.includes(phase.value);
                        const isDisabled = !isSelected && filters.phase.length >= 5;
                        return (
                          <label key={phase.value} className="flex items-center py-1 text-sm">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={isDisabled}
                              onChange={() => handleFilterChange('phase', phase.value)}
                              className="mr-2"
                            />
                            <span className={isDisabled ? 'text-gray-400' : ''}>
                              {phase.label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs text-gray-600">과목</label>
                    <input
                      type="text"
                      value={filters.subject}
                      onChange={(e) => handleFilterChange('subject', e.target.value)}
                      className="w-full rounded border px-3 py-2"
                      placeholder="예: 수학"
                    />
                  </div>

                  <div>
                    <button
                      onClick={handleSearchIds}
                      disabled={isLoadingIds}
                      className="flex h-10 w-full items-center justify-center gap-2 rounded-3xl bg-blue-500 px-3 text-white hover:bg-blue-600 disabled:opacity-50"
                    >
                      <Search className="size-4" />
                      {isLoadingIds ? '검색 중...' : '시험 조회'}
                    </button>
                  </div>
                </div>

                <div className="mt-4 flex gap-2">
                  {/* 추가 버튼들이 있다면 여기에 유지 */}
                  {/* 연결 테스트 버튼 주석처리 */}
                  {/* <button
                    onClick={async () => {
                      const isConnected = await testServerConnection();
                      alert(isConnected ? '서버 연결이 정상입니다.' : '서버 연결에 실패했습니다. 콘솔을 확인해주세요.');
                    }}
                    className="bg-gray-500 text-white px-3 py-2 rounded hover:bg-gray-600 text-sm"
                  >
                    연결 테스트
                  </button> */}
                </div>
              </div>

              {availableIds.length > 0 && (
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700">
                      조회된 시험 목록 ({availableIds.length}개)
                    </label>
                    <button
                      onClick={handleSelectAllIds}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {selectedIds.length === availableIds.length ? '전체 해제' : '전체 선택'}
                    </button>
                  </div>
                  <div className="max-h-32 overflow-y-auto rounded border bg-gray-50 p-2">
                    {availableIds.map(id => {
                      const examDetail = examDetails.find(exam => exam.sxm_id === id);
                      
                      return (
                        <label key={id} className="flex items-center rounded p-1 text-sm hover:bg-gray-100">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(id)}
                            onChange={() => handleIdToggle(id)}
                            className="mr-2"
                          />
                          {examDetail ? (
                            <div className="flex-1">
                              <div className="mb-1 flex items-center justify-between">
                                <div className="font-medium text-gray-800">
                                  ID: {id} | {examDetail.school_name} | {examDetail.att_year}년
                                </div>
                                <div className="flex items-center gap-1">
                                  {!examDetail.file_exists && (
                                    <span className="rounded bg-red-100 px-1 text-xs text-red-600">파일없음</span>
                                  )}
                                  {examDetail.analysis && getAnalysisStatusDisplay(examDetail.analysis)}
                                </div>
                              </div>
                              <div className="text-xs text-gray-600">
                                {grades.find(g => g.value === examDetail.school_grade)?.label || `${examDetail.school_grade}학년`} | 
                                {phases.find(p => p.value == examDetail.phase)?.label || `시험구분 ${examDetail.phase}`} | 
                                {examDetail.subject || '과목없음'}
                              </div>
                            </div>
                          ) : (
                            <span>시험 ID: {id}</span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                  <div className="mt-1 text-xs text-gray-600">
                    {selectedIds.length}개 선택됨
                  </div>
                </div>
              )}
                </>
              )}

              {/* 프리셋 리스트 탭 */}
              {modalTab === 'list' && (
                <div className="space-y-4">
                  {/* 안내 메시지 */}
                  <div className="rounded-3xl bg-blue-50 p-3 border border-blue-200">
                    <div className="flex items-start">
                      <Info className="mr-2 mt-0.5 size-4 text-blue-600 flex-shrink-0" />
                      <div className="text-sm text-blue-700">
                        <p className="font-medium">프리셋을 클릭하면 분석 결과 테이블에 컬럼이 추가됩니다.</p>
                        <p className="mt-1 text-xs">동일한 프리셋을 여러 번 선택하여 비교 분석이 가능합니다.</p>
                      </div>
                    </div>
                  </div>

                  {/* 검색 필터 */}
                  <div className="space-y-3 rounded-3xl border border-gray-200 bg-gray-50 p-4">
                    <h3 className="font-semibold text-gray-800">검색 필터</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          프리셋 이름
                        </label>
                        <input
                          type="text"
                          value={presetSearchParams.sxmset_name}
                          onChange={(e) => setPresetSearchParams(prev => ({ ...prev, sxmset_name: e.target.value }))}
                          placeholder="프리셋 이름 검색"
                          className="w-full rounded border p-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          작성자
                        </label>
                        <input
                          type="text"
                          value={presetSearchParams.author}
                          onChange={(e) => setPresetSearchParams(prev => ({ ...prev, author: e.target.value }))}
                          placeholder="작성자 이름 검색"
                          className="w-full rounded border p-2 text-sm"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setPresetSearchParams({
                            page: 1,
                            per_page: 20,
                            author: '',
                            sxmset_name: ''
                          });
                          fetchPresetList({
                            page: 1,
                            per_page: 20,
                            author: '',
                            sxmset_name: ''
                          });
                        }}
                        disabled={isLoadingPresets}
                        className="flex items-center justify-center gap-2 rounded-3xl bg-gray-500 px-4 py-2 text-white hover:bg-gray-600 disabled:opacity-50"
                        title="검색 조건 초기화"
                      >
                        <X className="size-4" />
                        초기화
                      </button>
                      <button
                        onClick={handlePresetSearch}
                        disabled={isLoadingPresets}
                        className="flex-1 flex items-center justify-center gap-2 rounded-3xl bg-primary px-4 py-2 text-white hover:bg-[#3da575] disabled:opacity-50"
                      >
                        <Search className="size-4" />
                        검색
                      </button>
                    </div>
                  </div>

                  {/* 프리셋 리스트 */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-800">
                        프리셋 목록 ({presetTotalItems}개)
                      </h3>
                      <div className="text-sm text-gray-600">
                        페이지 {presetSearchParams.page} / {presetTotalPages}
                      </div>
                    </div>

                    {isLoadingPresets ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="text-gray-500">로딩 중...</div>
                      </div>
                    ) : presetList.length === 0 ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="text-gray-500">등록된 프리셋이 없습니다.</div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {presetList.map((preset) => (
                          <div
                            key={preset.sxmset_id}
                            className={`rounded-3xl border border-gray-200 bg-white p-3 transition-colors ${
                              loadingPresetId === preset.sxmset_id
                                ? 'opacity-50 cursor-wait'
                                : editingPresetId !== preset.sxmset_id
                                ? 'cursor-pointer hover:bg-gray-50'
                                : ''
                            }`}
                            onClick={() => {
                              if (editingPresetId !== preset.sxmset_id && loadingPresetId === null) {
                                handlePresetSelect(preset);
                              }
                            }}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                {editingPresetId === preset.sxmset_id ? (
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="text"
                                      value={editingPresetName}
                                      onChange={(e) => setEditingPresetName(e.target.value)}
                                      onClick={(e) => e.stopPropagation()}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          handlePresetUpdate(preset.sxmset_id);
                                        } else if (e.key === 'Escape') {
                                          handlePresetEditCancel();
                                        }
                                      }}
                                      className="flex-1 rounded border px-2 py-1 text-sm font-medium"
                                      placeholder="프리셋 이름"
                                      autoFocus
                                    />
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handlePresetUpdate(preset.sxmset_id);
                                      }}
                                      className="rounded p-1 text-green-600 hover:bg-green-50"
                                      title="저장"
                                    >
                                      <Check className="size-4" />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handlePresetEditCancel();
                                      }}
                                      className="rounded p-1 text-gray-600 hover:bg-gray-50"
                                      title="취소"
                                    >
                                      <XCircle className="size-4" />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    {loadingPresetId === preset.sxmset_id ? (
                                      <Loader className="size-4 text-blue-600 animate-spin" />
                                    ) : (
                                      <FileCheck className="size-4 text-green-600" />
                                    )}
                                    <h4 className="font-medium text-gray-900">
                                      {preset.sxmset_name}
                                    </h4>
                                    {loadingPresetId === preset.sxmset_id && (
                                      <span className="text-xs text-blue-600">분석 중...</span>
                                    )}
                                  </div>
                                )}
                                <div className="mt-1 text-sm text-gray-600">
                                  <p>작성자: {preset.author}</p>
                                  <p>생성일: {new Date(preset.created_at).toLocaleDateString('ko-KR')}</p>
                                  {preset.updated_at !== preset.created_at && (
                                    <p>수정일: {new Date(preset.updated_at).toLocaleDateString('ko-KR')}</p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-start gap-2">
                                <div className="text-sm text-gray-500">
                                  ID: {preset.sxmset_id}
                                </div>
                                {editingPresetId !== preset.sxmset_id && (
                                  <div className="flex gap-1">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handlePresetEditStart(preset);
                                      }}
                                      className="rounded p-1 text-blue-600 hover:bg-blue-50"
                                      title="수정"
                                    >
                                      <Edit2 className="size-4" />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handlePresetDelete(preset.sxmset_id, preset.sxmset_name);
                                      }}
                                      className="rounded p-1 text-red-600 hover:bg-red-50"
                                      title="삭제"
                                    >
                                      <Trash2 className="size-4" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* 페이지네이션 */}
                    {presetTotalPages > 1 && (
                      <div className="flex items-center justify-center space-x-2 pt-4">
                        <button
                          onClick={() => handlePresetPageChange(presetSearchParams.page - 1)}
                          disabled={presetSearchParams.page <= 1 || isLoadingPresets}
                          className="rounded bg-gray-200 px-3 py-1 text-sm hover:bg-gray-300 disabled:opacity-50"
                        >
                          이전
                        </button>
                        <span className="text-sm text-gray-600">
                          {presetSearchParams.page} / {presetTotalPages}
                        </span>
                        <button
                          onClick={() => handlePresetPageChange(presetSearchParams.page + 1)}
                          disabled={presetSearchParams.page >= presetTotalPages || isLoadingPresets}
                          className="rounded bg-gray-200 px-3 py-1 text-sm hover:bg-gray-300 disabled:opacity-50"
                        >
                          다음
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex shrink-0 justify-end gap-2 border-t p-6">
              <button
                onClick={handleCloseModal}
                className="rounded-3xl bg-gray-500 px-4 py-2 text-white hover:bg-gray-600"
              >
                {modalTab === 'list' ? '닫기' : '취소'}
              </button>
              {modalTab === 'add' && (
                <button
                  onClick={handleAddColumn}
                  disabled={isLoading || !newColumnName.trim() || selectedIds.length === 0}
                  className="rounded-3xl bg-primary px-4 py-2 text-white hover:bg-[#3da575] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isLoading ? '등록 중...' : '프리셋 등록'}
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* 학교별 합산 정보 모달 */}
      {showSchoolSummaryModal && createPortal(
        <div 
          className="fixed inset-0 flex items-center justify-center transition-all"
          style={{ 
            zIndex: 9999980,
            paddingTop: isMobileView ? 'env(safe-area-inset-top, 0px)' : '0px',
            paddingBottom: isMobileView ? 'calc(68px + env(safe-area-inset-bottom, 0px))' : '0px',
            backgroundColor: 'rgba(0, 0, 0, 0.3)'
          }}
          onClick={() => setShowSchoolSummaryModal(false)}
        >
          <div 
            className="flex flex-col rounded-3xl bg-white shadow-xl transition-all w-[95vw] lg:w-[960px]"
            style={{
              height: isMobileView ? 'calc((100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)) * 0.75)' : '90vh',
              maxHeight: isMobileView ? 'calc((100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)) * 0.75)' : '90vh'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* 헤더 */}
            <div className="flex shrink-0 items-center justify-between border-b p-4">
              <h2 className="text-xl font-bold text-gray-800">데이터 정보</h2>
              <button
                onClick={() => setShowSchoolSummaryModal(false)}
                className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                aria-label="닫기"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* 바디 */}
            <div className="flex-1 p-4 overflow-y-auto">
              <div className="space-y-4">
                {/* 컬럼 정보 */}
                <div className="rounded-3xl bg-blue-50 p-3">
                  <span className="text-sm font-medium text-blue-700">
                    {columns.length}개 데이터 기준
                  </span>
                </div>
                
                {/* 컬럼별 데이터 그리드 */}
                <div className="grid grid-cols-2 gap-4">
                  {columns.map((column, index) => {
                    // 컬럼의 시험 정보 집계
                    const examInfo = column.examDetails || [];
                    const schools = [...new Set(examInfo.map(e => e.school_name))];
                    const grades = [...new Set(examInfo.map(e => e.school_grade))];
                    const phases = [...new Set(examInfo.map(e => e.phase))];
                    const subjects = [...new Set(examInfo.map(e => e.subject).filter(s => s))];
                    
                    // 학년 정보 포맷팅
                    const gradeLabels = grades.map(g => {
                      const gradeNum = parseInt(g);
                      if (gradeNum <= 6) return `${gradeNum}학년(초)`;
                      else if (gradeNum <= 9) return `${gradeNum - 6}학년(중)`;
                      else return `${gradeNum - 9}학년(고)`;
                    });
                    
                    // 시험 단계 정보 포맷팅
                    const phaseLabels = phases.map(p => {
                      if (p === '11' || p === 11) return '1학기 중간';
                      if (p === '12' || p === 12) return '1학기 기말';
                      if (p === '13' || p === 13) return '1학기 기타';
                      if (p === '21' || p === 21) return '2학기 중간';
                      if (p === '22' || p === 22) return '2학기 기말';
                      if (p === '23' || p === 23) return '2학기 기타';
                      return '기타';
                    });
                    
                    return (
                      <div key={column.id} className="rounded-3xl border border-gray-200 bg-white p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="text-sm font-semibold text-gray-900 mb-2">
                              {column.name}
                            </div>
                            <div className="space-y-1">
                              {schools.length > 0 && (
                                <div className="text-xs text-gray-600">
                                  <span className="font-medium">학교:</span> {schools.join(', ')}
                                </div>
                              )}
                              {gradeLabels.length > 0 && (
                                <div className="text-xs text-gray-600">
                                  <span className="font-medium">학년:</span> {gradeLabels.join(', ')}
                                </div>
                              )}
                              {phaseLabels.length > 0 && (
                                <div className="text-xs text-gray-600">
                                  <span className="font-medium">시험:</span> {phaseLabels.join(', ')}
                                </div>
                              )}
                              {subjects.length > 0 && (
                                <div className="text-xs text-gray-600">
                                  <span className="font-medium">과목:</span> {subjects.join(', ')}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 ml-2">
                            <div 
                              className={`w-3 h-3 rounded-sm bg-gradient-to-r ${
                                columnColors ? columnColors[index % columnColors.length] : 'from-gray-400 to-gray-500'
                              }`}
                            />
                          </div>
                        </div>
                        
                        <div className="mt-3 pt-3 border-t">
                          <div className="flex items-baseline gap-1">
                            <span className="text-xs text-gray-500">총</span>
                            <span className="text-lg font-bold text-green-600">
                              {column.sxmIds?.length || 0}
                            </span>
                            <span className="text-xs text-gray-500">개 시험</span>
                            <span className="text-xs text-gray-500">(총</span>
                            <span className="text-lg font-bold text-green-600">
                              {Object.values(column.problemCounts || {}).reduce((a, b) => a + b, 0)}
                            </span>
                            <span className="text-xs text-gray-500">문제)</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* 전체 통계 */}
                <div className="rounded-3xl bg-gray-50 p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-sm font-medium text-gray-700">추가 데이터 수</div>
                      <div className="text-2xl font-bold text-gray-900">{columns.length}개</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-700">전체 분석 데이터</div>
                      <div className="text-2xl font-bold text-primary">
                        총 {columns.reduce((sum, col) => sum + (col.sxmIds?.length || 0), 0)}개 시험
                        (총 {columns.reduce((sum, col) => {
                          const problemSum = Object.values(col.problemCounts || {}).reduce((a, b) => a + b, 0);
                          return sum + problemSum;
                        }, 0)}문제)
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* 매스컨셉 상세 정보 모달 - 공통 컴포넌트 사용 (Loader, 닫기 버튼, Bearer prefix, 401 처리) */}
      <MathceptDetailModal
        isOpen={detailModalOpen}
        onClose={closeDetailModal}
        data={detailData}
        loading={detailLoading}
        renderLatex={renderLatex}
        showCloseButton={true}
        useLoader={true}
        handleAuth={true}
        useBearerPrefix={true}
      />
    </div>
  );
}

export default AnalysisResultTable; 